import { useDirtySinceOpen } from '../hooks/useDirtySinceOpen';
import { finalizeBehaviorAiFeedbackAfterSave } from '../services/recordAiFeedbackService';
import AiRecordFeedbackPanel, { type AiRecordFeedbackPanelHandle } from '../components/program/AiRecordFeedbackPanel';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import TodoNoteItemAddModal from '../components/todoNote/TodoNoteItemAddModal';
import TodoRoutineCreateModal from '../components/todoNote/TodoRoutineCreateModal';
import { DEFAULT_TODO_CATEGORY_NAME, joinTodoMemo } from '../components/todoNote/todoNoteRowShared';
import TodoNoteBoardPicker from '../components/todoNote/TodoNoteBoardPicker';
import TodoPostitCardActions from '../components/todoNote/TodoPostitCardActions';
import { formatTodoPostitDate, buildTodoPostitTiles, TODO_POSTIT_STACK_LAYER_MAX } from '../utils/todoNotePostitLayout';
import { isTodoNoteFavorite } from '../utils/todoNoteFavorite';
import ClassRecordMemoNoteEditor, {
  type ClassRecordMemoNoteEditorHandle,
} from '../components/program/ClassRecordMemoNoteEditor';
import RecordMemberGroupSelect from '../components/program/RecordMemberGroupSelect';
import DeleteHoldButton from '../components/common/DeleteHoldButton';
import { useSealedMemoTagLabels } from '../hooks/useSealedMemoTagLabels';
import { toISODate, addDays, parseISODate } from '../utils/date';
import { addDaysForDashboardNav, dateToWeekdayKey, mondayIsoForDateInLocal } from '../utils/dashboardDateContext';
import { shouldIgnoreAppNavigationShortcut } from '../utils/appNavigationShortcutGuard';
import type { DashboardAnnualDaySlot } from '../utils/dashboardAnnualDaySlots';
import {
  buildDashboardWeeklyDaySlots,
} from '../utils/dashboardAnnualDaySlots';
import { loadTodoNoteWorkspaceCache } from '../utils/todoNoteWorkspaceCache';
import { mapProgressLikeToDisplayFields } from '../utils/annualProgressFieldDisplay';
import { annualSlotHasTimetableProgramAssignment } from '../utils/annualProgressSlotSource';
import { resolveClassScheduleSettingsFromLocalCaches } from '../utils/classLessonAlarmSchedule';
import { isWeekdayTimeKey, resolveClassScheduleRowsForWeekday } from '../utils/classScheduleWeekdayTimes';
import { resolveClassEntryByGradeId } from '../utils/programProgressRows';
import {
  collectDashboardVisibleLessonTermNumbers,
  dashboardLessonTermContentNeedsWarm,
  warmDashboardLessonTermContent,
} from '../utils/dashboardCalendarLessonTermWarm';
import { useDashboardLessonSectionData } from '../hooks/useDashboardLessonSectionData';
import { useCalendarLinkedContext } from '../hooks/useCalendarLinkedContext';
import { useDailyNoteViewLockEnabled } from '../hooks/useDailyNoteViewLockEnabled';
import DailyNoteLockModal from '../components/dailyNote/DailyNoteLockModal';
import {
  DashboardWeeklyTodoItems,
  weeklyBehaviorRecordKey,
} from '../components/dashboard/DashboardWeeklyPlanner';
import WeeklyLearningGuidePage, {
  type WeeklyLearningGuideEmbeddedNavControl,
  type WeeklyLearningGuideEmbeddedNavState,
  type WeeklyLearningGuideEmbeddedOptions,
} from './WeeklyLearningGuidePage';
import { DashboardLessonEvalStatusBadge } from '../components/dashboard/DashboardLessonEvalStatusBadge';
import DashboardWeeklyWeekPane from '../components/dashboard/DashboardWeeklyWeekPane';
import DashboardWeeklyCalendar from '../components/dashboard/DashboardWeeklyCalendar';
import { useDashboardWeeklyWeekSwipe } from '../hooks/useDashboardWeeklyWeekSwipe';
import { shiftMondayIso } from '../utils/dashboardWeeklyWeekSwipe';
import {
  readDashboardWeeklyCalendarLayout,
  writeDashboardWeeklyCalendarLayout,
  readDashboardCalendarFixedEventsOnly,
  writeDashboardCalendarFixedEventsOnly,
  type DashboardWeeklyCalendarLayoutMode,
} from '../utils/dashboardWeeklyCalendar';
import { openDashboardAnnualSlotPresentation } from '../utils/openDashboardAnnualSlotPresentation';
import { buildDashboardSlotProgramPath } from '../utils/dashboardSlotProgramPath';
import DashboardWeeklyLessonActionMenu from '../components/dashboard/DashboardWeeklyLessonActionMenu';
import DashboardYearCalendars from '../components/dashboard/DashboardYearCalendars';
import DashboardPlannerSectionHead from '../components/dashboard/DashboardPlannerSectionHead';
import DashboardSemesterWeekDigit from '../components/dashboard/DashboardSemesterWeekDigit';
import { resolveSemesterWeekIndex, weekDateIsosFromMonday, formatDashboardWeekMmDd } from '../utils/dashboardSemesterWeek';
import {
  fetchDailyRecordNotesForWeek,
  saveDailyRecordNotesWeek,
} from '../services/dailyRecordNotesService';
import {
  fetchDailyNoteCategories,
  peekDailyNoteCategoriesCache,
} from '../services/dailyNoteCategoriesService';
import {
  clearDailyRecordNotesWeekRowsDraft,
  loadDailyRecordNotesWeekRowsCache,
  overlayDailyRecordNotesWeekRowsDraft,
  persistDailyRecordNotesWeekRowsDraft,
} from '../utils/dailyRecordNotesCache';
import type { DailyNoteCategoryRow, DailyWeekNoteDraft } from '../types/dailyRecordNote';
import {
  defaultDailyCategoryId,
  mergeWeekNotesKeepingLocalDirty,
  prepareWeekNotesForSave,
  weekNotesSignature,
} from '../utils/dailyRecordNotesWeekState';
import { useAutoStatusToast } from '../hooks/useAutoStatusToast';
import AutoStatusToast from '../components/common/AutoStatusToast';
import { resolveTermNumberForViewDate } from '../utils/dashboardLessonSectionResolve';
import { clampDateToPlannerWeeks, clampMonthToPlanner, plannerMinMaxMondayIso, buildDashboardMonthCells, dashboardPlannerBounds } from '../utils/dashboardYearMonths';
import { collectAcademicCalendarHolidayCache, mergeOffDaysIntoHolidayCache } from '../utils/dashboardAcademicHolidays';
import {
  readDashboardLessonViewMode,
  writeDashboardLessonViewMode,
  type DashboardLessonViewMode,
} from '../utils/dashboardLessonViewMode';
import {
  DASHBOARD_WEEKLY_DAYS_VERTICAL_MAX_WIDTH_PX,
  matchDashboardWeeklyDaysAutoVertical,
  readDashboardWeeklyDaysVerticalPref,
  writeDashboardWeeklyDaysVerticalPref,
} from '../utils/dashboardWeeklyDaysLayout';
import { ACADEMIC_SECTION_CALENDAR_CHANGE_EVENT } from '../utils/calendarSectionAcademicYear';
import {
  ANNUAL_TIMETABLE_SAVED_EVENT,
  type AnnualTimetableSavedDetail,
} from '../utils/annualTimetableSavedEvents';
import {
  emitTodoNoteBoardSelection,
  TODO_NOTE_BOARD_SELECTION_EVENT,
  type TodoNoteBoardSelectionDetail,
} from '../utils/todoNoteBoardSelectionEvents';
import {
  emitTodoNoteWorkspaceSaved,
  TODO_NOTE_WORKSPACE_SAVED_EVENT,
  type TodoNoteWorkspaceSavedDetail,
} from '../utils/todoNoteWorkspaceSavedEvents';
import {
  loadTodoNoteSelectedBoardPreference,
  resolveTodoNoteSelectedBoardId,
  saveTodoNoteSelectedBoardPreference,
} from '../utils/todoNoteSelectedBoardPreference';
import {
  loadTodoShareBoardContentCache,
  saveTodoShareBoardContentCache,
} from '../utils/todoShareBoardContentCache';
import { isTodoShareDefaultBoard, sortTodoShareBoards, todoShareNameFromTitle } from '../utils/todoShareTitle';
import type { TodoNoteWorkspaceSnapshot } from '../utils/todoNoteWorkspaceCache';
import {
  PROGRAM_PROGRESS_SAVED_EVENT,
  type ProgramProgressSavedDetail,
  emitProgramProgressSaved,
} from '../utils/programProgressSavedEvents';
import {
  parseMemoFieldStorageDeep,
  serializeClassRecordCellStorage,
} from '../utils/programCellDisplayText';
import {
  buildDefaultEvalMemberTagsNote,
  evalSerializedNoteHasNonTagText,
} from '../utils/classRecordMemoHashtags';
import { emitAnnualBehaviorNotesSaved } from '../utils/annualBehaviorNotesSavedEvents';
import { Grade, type ProgramScopeType } from '../types/domain';
import { saveTodoNoteWorkspace } from '../services/todoNoteService';
import {
  buildTodoShareSavePayload,
  listTodoShareBoards,
  loadTodoShareAsOwner,
  migratePersonalTodoToDefaultBoard,
  saveTodoShareAsOwner,
  type TodoShareBoardMeta,
} from '../services/todoShareService';
import { setTodoNoteWorkspaceQueryData } from '../query/noteCacheUpdates';
import { useTodoNoteWorkspaceQuery } from '../query/hooks/useTodoNoteWorkspaceQuery';
import { saveScopedRecordFieldsAtPosition } from '../utils/scopedRecordSave';
import { upsertProgramFixedContentRows } from '../services/programFixedContentService';
import {
  readProgramFixedRowsCacheOrQuery,
} from '../query/readSchoolQueryCache';
import {
  clearProgramLessonRecordCaches,
} from '../utils/annualProgressClientCacheInvalidation';
import {
  deleteAnnualBehaviorNoteByNaturalKey,
  upsertAnnualBehaviorNote,
} from '../services/annualBehaviorNotesService';
import { useAnnualBehaviorNotesQuery } from '../query/hooks/useAnnualBehaviorNotesQuery';
import { getClassMemberGradeLayoutsMap, getClassMembers } from '../services/classMembersService';
import type { ClassMemberGradeLayout, ClassMemberRow } from '../utils/classMembersStorage';
import {
  firstRecordGroupId,
  membersForRecordGroup,
  matchMembersByClassRecordTagKeyword,
  resolveDefaultRecordGroupId,
  type MemberWithGroupContext,
} from '../utils/classMemberRecordGroupUtils';
import { loadSelectedClassGroupId } from '../utils/selectedClassGroupPreference';
import type { AnnualBehaviorNote } from '../types/domain';
import type { TodoNoteCategoryDraft, TodoNoteItemDraft, TodoNoteItemView, TodoRoutineConfig } from '../types/todoNote';
import { compareTodoItemsForSort, todoItemBucketKey } from '../utils/todoNoteBucket';
import { expandTodoItemsForView, normalizeRoutineDayLogs, parseRoutineOccurrenceId, patchRoutineDayLog, toggleRoutineOccurrenceComplete } from '../utils/todoRoutine';
import { listDashboardDailyIncompleteTodos } from '../utils/dashboardDailyTodos';
import { matchesMaxWidth, PAGE_ZOOM_CHANGED_EVENT } from '../utils/pageZoom';
import { supabase } from '../supabaseClient';
import TodoChecklistText from '../components/todoNote/TodoChecklistText';
import {
  dailyNoteLockPasswordMatches,
  loadDailyNoteViewLock,
  setDailyNoteViewSessionUnlocked,
} from '../utils/dailyNoteViewLockStorage';

const EMPTY_SECTION_MESSAGE =
  '데이터 구성 전입니다. 데이터가 구성되면 해당 섹션에 정보가 표시됩니다.';

const NO_LESSON_LABEL = '수업 없음';

function formatTodoDueLabel(item: TodoNoteItemDraft): string {
  const iso = String(item.due_date ?? '').trim();
  if (!iso) return '-';
  if (item.due_kind === 'month') {
    const [y, m] = iso.split('-');
    if (!y || !m) return iso;
    return `${y}.${m}`;
  }
  return iso;
}

function isDashboardTodoActionTarget(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest('.cp-todo-note-postit-actions'));
}

function todoCardBackgroundStyle(color: string | undefined): React.CSSProperties | undefined {
  if (!color || !/^#[0-9A-Fa-f]{6}$/.test(color)) return undefined;
  return { background: `color-mix(in srgb, ${color} 18%, var(--cp-dashboard-slot-surface))` };
}

function todoCategoryDotStyle(color: string | undefined): React.CSSProperties | undefined {
  if (!color || !/^#[0-9A-Fa-f]{6}$/.test(color)) return undefined;
  return { backgroundColor: color };
}

function formatTodoYearLine(item: TodoNoteItemDraft): string {
  const iso = String(item.due_date ?? '').trim();
  if (!iso) return '-';
  const [y] = iso.split('-');
  return y ? `${y}년` : '-';
}

function formatTodoDueMainLine(item: TodoNoteItemDraft): string {
  const iso = String(item.due_date ?? '').trim();
  if (!iso) return '-';
  const [ys, ms, ds] = iso.split('-');
  const y = Number(ys);
  const m = Number(ms);
  const d = Number(ds);
  if (item.due_kind === 'month') {
    return Number.isFinite(m) ? `${m}월 (월단위)` : formatTodoDueLabel(item);
  }
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return formatTodoDueLabel(item);
  const wd = (['일', '월', '화', '수', '목', '금', '토'] as const)[new Date(y, m - 1, d).getDay()];
  return `${m}월 ${d}일 (${wd})`;
}

function formatLearning(d: ReturnType<typeof mapProgressLikeToDisplayFields>): string {
  const main = (d.progressLearning ?? '').trim();
  const note = (d.progressLearningNote ?? '').trim();
  if (main && note) return `${main}\n${note}`;
  return main || note;
}

/** 연간: 시간표 칸이 비어 있으면 진도를 붙이지 않는 것과 같게, ‘수업 없음’ */
function isDashboardSlotNoLesson(slot: DashboardAnnualDaySlot): boolean {
  if (slot.timetableSlotKind === 'out_of_term' || slot.timetableSlotKind === 'holiday') return true;
  return !annualSlotHasTimetableProgramAssignment(slot.timetableText, slot.timetableColorHex);
}

function formatUnitPart(d: ReturnType<typeof mapProgressLikeToDisplayFields>): string {
  return (d.progressUnit ?? '').trim();
}

/** 쪽수(보조쪽수). 보조가 없으면 괄호 없이 쪽수만. */
function formatPageParen(d: ReturnType<typeof mapProgressLikeToDisplayFields>): string {
  const page = (d.progressPage ?? '').trim();
  const asst = (d.progressAssistantPage ?? '').trim();
  if (!page && !asst) return '';
  if (page && asst) return `${page} (${asst})`;
  if (page) return page;
  return `(${asst})`;
}

function formatPeriodTimeRange(startTime?: string, endTime?: string): string {
  const start = (startTime ?? '').trim();
  const end = (endTime ?? '').trim();
  if (start && end) return `${start} - ${end}`;
  return start || end;
}

function splitPeriodTimeLabel(timeLabel?: string): { start: string; end: string } | null {
  const raw = (timeLabel ?? '').trim();
  if (!raw) return null;
  const matched = /^(.+?)\s*[-–—~～]\s*(.+)$/.exec(raw);
  if (matched) return { start: matched[1].trim(), end: matched[2].trim() };
  return { start: raw, end: '' };
}

function DashboardPeriodRail({
  periodLabel,
  timeLabel,
}: {
  periodLabel: string;
  timeLabel?: string;
}) {
  const timeParts = splitPeriodTimeLabel(timeLabel);
  return (
    <div className="cp-dashboard-slot__period-rail">
      <span className="cp-dashboard-slot__period-label">{periodLabel}</span>
      {timeParts ? (
        <span className="cp-dashboard-slot__period-time">
          <span className="cp-dashboard-slot__period-time-start">{timeParts.start}</span>
          {timeParts.end ? (
            <>
              <span className="cp-dashboard-slot__period-time-sep" aria-hidden>
                {' - '}
              </span>
              <span className="cp-dashboard-slot__period-time-end">{timeParts.end}</span>
            </>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

function DashboardLessonSummaryMain({
  pyeonje,
  unitPart,
  learning,
  pagePart,
  evalCode,
  evalLabel,
  evalNote,
}: {
  pyeonje: string;
  unitPart: string;
  learning: string;
  pagePart?: string;
  evalCode?: string;
  evalLabel?: string;
  evalNote?: string;
}) {
  const evalTitle = [evalLabel, evalNote].map((s) => (s ?? '').trim()).filter(Boolean).join(' · ');
  const hasEval = Boolean((evalCode ?? '').trim() || (evalLabel ?? '').trim() || (evalNote ?? '').trim());
  const metaTitle = [pyeonje, unitPart, pagePart].filter(Boolean).join(' ');

  return (
    <>
      <div className="cp-dashboard-slot__daily-meta" title={metaTitle || undefined}>
        {pyeonje ? <span className="cp-dashboard-slot__pyeonje-label">{pyeonje}</span> : null}
        {unitPart || hasEval ? (
          <span className="cp-dashboard-slot__daily-meta-unit-group">
            {unitPart ? <span className="cp-dashboard-slot__unit-name">{unitPart}</span> : null}
            {hasEval ? (
              <div
                className="cp-dashboard-slot__summary-col cp-dashboard-slot__summary-col--eval"
                title={evalTitle || undefined}
              >
                <DashboardLessonEvalStatusBadge code={evalCode} label={evalLabel} />
                {(evalNote ?? '').trim() ? (
                  <span className="cp-dashboard-slot__eval-note">{(evalNote ?? '').trim()}</span>
                ) : null}
              </div>
            ) : null}
          </span>
        ) : null}
        {pagePart ? <span className="cp-dashboard-slot__unit-pages">{pagePart}</span> : null}
      </div>
      {learning ? (
        <p className="cp-dashboard-slot__row-learning" title={learning}>
          {learning}
        </p>
      ) : null}
    </>
  );
}

function mapTodoDashboardRow(
  item: TodoNoteItemView,
  categoryById: Map<string, TodoNoteCategoryDraft>
): { item: TodoNoteItemView; category: TodoNoteCategoryDraft | null } {
  return {
    item,
    category: item.category_id ? categoryById.get(item.category_id) ?? null : null,
  };
}

export interface DashboardPageProps {
  workspaceId: string;
  currentGradeId: string;
  grades: Grade[];
  onSwitchToClassIntegrated?: () => void;
}

const TODO_COMPLETE_HOLD_MS = 2000;
/** 길게 누르기: 이 시간(ms) 이후에만 완료 게이지 시작 */
const TODO_COMPLETE_HOLD_DELAY_MS = 500;

const DashboardPage: React.FC<DashboardPageProps> = ({ workspaceId, currentGradeId, grades = [] }) => {
  const navigate = useNavigate();
  const [viewDate, setViewDate] = useState(() => new Date());
  const [lessonRefreshNonce, setLessonRefreshNonce] = useState(0);
  const [userId, setUserId] = useState('');
  /** false: 오늘~2주 창만, true: 전체 TODO(미완료) */
  const [todoExpanded, setTodoExpanded] = useState(false);
  /** Todo 추가 모달 */
  const [todoAddModalOpen, setTodoAddModalOpen] = useState(false);
  const [todoAddIsDuplicate, setTodoAddIsDuplicate] = useState(false);
  const [todoAddDraft, setTodoAddDraft] = useState<TodoNoteItemDraft | null>(null);
  const [todoAddCategoryInput, setTodoAddCategoryInput] = useState('');
  const [todoAddSaving, setTodoAddSaving] = useState(false);
  const [todoBoards, setTodoBoards] = useState<TodoShareBoardMeta[]>([]);
  const [todoSelectedBoardId, setTodoSelectedBoardId] = useState<string | null>(null);
  const [todoBoardPickerOpen, setTodoBoardPickerOpen] = useState(false);
  const [todoAltSnapshot, setTodoAltSnapshot] = useState<TodoNoteWorkspaceSnapshot | null>(null);
  const [todoAltLoading, setTodoAltLoading] = useState(false);
  const [todoAltError, setTodoAltError] = useState<string | null>(null);
  const [todoEditModalOpen, setTodoEditModalOpen] = useState(false);
  const [todoEditDraft, setTodoEditDraft] = useState<TodoNoteItemDraft | null>(null);
  const [todoEditCategoryInput, setTodoEditCategoryInput] = useState('');
  const [todoEditSaving, setTodoEditSaving] = useState(false);
  const [todoRoutineModalOpen, setTodoRoutineModalOpen] = useState(false);
  const [todoRoutineDraft, setTodoRoutineDraft] = useState<TodoNoteItemDraft | null>(null);
  const [todoRoutineCategoryInput, setTodoRoutineCategoryInput] = useState('');
  const [todoRoutineSaving, setTodoRoutineSaving] = useState(false);
  const [todoRoutineOccurrenceYmd, setTodoRoutineOccurrenceYmd] = useState<string | null>(null);
  const [todoRoutineCompleteMark, setTodoRoutineCompleteMark] = useState<'v' | 'x' | null>(null);
  const [todoRoutineCompleteTouched, setTodoRoutineCompleteTouched] = useState(false);
  const [selectedTodoItemId, setSelectedTodoItemId] = useState<string | null>(null);
  const [lessonViewMode, setLessonViewMode] = useState<DashboardLessonViewMode>(() =>
    readDashboardLessonViewMode()
  );
  const [weeklyDaysVerticalPref, setWeeklyDaysVerticalPref] = useState(() =>
    readDashboardWeeklyDaysVerticalPref()
  );
  const [weeklyDaysAutoVertical, setWeeklyDaysAutoVertical] = useState(() =>
    matchDashboardWeeklyDaysAutoVertical()
  );
  const [calendarLayoutMode, setCalendarLayoutMode] = useState<DashboardWeeklyCalendarLayoutMode>(() =>
    readDashboardWeeklyCalendarLayout()
  );
  const [calendarFixedEventsOnly, setCalendarFixedEventsOnly] = useState(() =>
    readDashboardCalendarFixedEventsOnly()
  );
  const [isMobileCalendarLayout, setIsMobileCalendarLayout] = useState(() => matchesMaxWidth(900));
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  const [yearCalendarModalOpen, setYearCalendarModalOpen] = useState(false);
  /** 위클리: 수업 면 ↔ 일자별 기록 면 */
  const [weeklyNotesMode, setWeeklyNotesMode] = useState(false);
  /** 시간표 모드: true면 교시 대신 요일별 TODO 포스트잇 */
  const [timetableTodoMode, setTimetableTodoMode] = useState(false);
  const [timetableMondayIso, setTimetableMondayIso] = useState('');
  const [timetableNavState, setTimetableNavState] = useState<WeeklyLearningGuideEmbeddedNavState | null>(
    null
  );
  const timetableNavControlRef = useRef<WeeklyLearningGuideEmbeddedNavControl | null>(null);
  const [dailyWeekNotes, setDailyWeekNotes] = useState<DailyWeekNoteDraft[]>([]);
  const [dailyWeekNotesBaseline, setDailyWeekNotesBaseline] = useState<DailyWeekNoteDraft[]>([]);
  const [dailyNoteCategories, setDailyNoteCategories] = useState<DailyNoteCategoryRow[]>([]);
  const dailyNotesBaselineRef = useRef(dailyWeekNotesBaseline);
  dailyNotesBaselineRef.current = dailyWeekNotesBaseline;
  const loadedDailyNotesWeekKeyRef = useRef<string | null>(null);
  const [dailyNotesSaving, setDailyNotesSaving] = useState(false);
  const { toastState: dailyNotesToast, showSaving: showDailyNotesSaving, showSuccess: showDailyNotesSuccess, showError: showDailyNotesError } =
    useAutoStatusToast(2800);
  const { contentsHidden: dailyNoteViewLocked } = useDailyNoteViewLockEnabled(userId);
  const [weeklyNoteUnlockOpen, setWeeklyNoteUnlockOpen] = useState(false);
  const [weeklyNoteUnlockBusy, setWeeklyNoteUnlockBusy] = useState(false);
  const [weeklyNoteUnlockError, setWeeklyNoteUnlockError] = useState('');
  /** 수업 섹션: 선택된 슬롯 인덱스 */
  const [selectedLessonSlotIndex, setSelectedLessonSlotIndex] = useState<number | null>(null);
  /** 위클리 등에서 daySlots 밖 슬롯을 직접 지정할 때 */
  const [pinnedLessonSlot, setPinnedLessonSlot] = useState<DashboardAnnualDaySlot | null>(null);
  /** 데일리 수업 블럭 미니 모달 */
  const [dailyLessonMenu, setDailyLessonMenu] = useState<{
    slot: DashboardAnnualDaySlot;
    rect: DOMRect;
  } | null>(null);
  /** 멤버 그룹 레이아웃 */
  const [memberGradeLayout, setMemberGradeLayout] = useState<ClassMemberGradeLayout>({ groups: [], membersByGroupId: {} });
  const [classMembers, setClassMembers] = useState<ClassMemberRow[]>([]);
  /** 수업기록 모달 */
  const [lessonRecordModalOpen, setLessonRecordModalOpen] = useState(false);
  const [lessonRecordNote, setLessonRecordNote] = useState('');
  const [lessonRecordTab, setLessonRecordTab] = useState<'lesson' | 'eval_log'>('lesson');
  const [lessonRecordNoteEval, setLessonRecordNoteEval] = useState('');
  const [lessonRecordMemberGroupId, setLessonRecordMemberGroupId] = useState<string | null>(null);
  const [lessonRecordEvalMemoMemberGroupId, setLessonRecordEvalMemoMemberGroupId] = useState<string | null>(null);
  const [lessonEvalTagGenConfirm, setLessonEvalTagGenConfirm] = useState<{
    nextNote: string;
    sourceGroupId: string;
  } | null>(null);
  const [lessonRecordModalExpanded, setLessonRecordModalExpanded] = useState(false);
  const [lessonRecordSaving, setLessonRecordSaving] = useState(false);
  const [lessonRecordError, setLessonRecordError] = useState<string | null>(null);
  const lessonRecordMemoEditorRef = useRef<ClassRecordMemoNoteEditorHandle | null>(null);
  const lessonRecordEvalEditorRef = useRef<ClassRecordMemoNoteEditorHandle | null>(null);
  /** 수업기록: 태그 그룹 선택 모달 */
  const [lessonTagGroupPick, setLessonTagGroupPick] = useState<{
    keyword: string;
    candidates: MemberWithGroupContext[];
  } | null>(null);
  /** 행동특성 모달 */
  const [behaviorNoteModalOpen, setBehaviorNoteModalOpen] = useState(false);
  const [behaviorNote, setBehaviorNote] = useState('');
  const [behaviorNoteMemberGroupId, setBehaviorNoteMemberGroupId] = useState<string | null>(null);
  const [behaviorNoteModalExpanded, setBehaviorNoteModalExpanded] = useState(false);
  const [behaviorNoteSaving, setBehaviorNoteSaving] = useState(false);
  const [behaviorNoteError, setBehaviorNoteError] = useState<string | null>(null);
  const sealedLabelByToken = useSealedMemoTagLabels([
    lessonRecordNote,
    lessonRecordNoteEval,
    behaviorNote,
  ]);
  const behaviorMemoEditorRef = useRef<ClassRecordMemoNoteEditorHandle | null>(null);
  /** 행동특성: 태그 그룹 선택 모달 */
  const [behaviorTagGroupPick, setBehaviorTagGroupPick] = useState<{
    keyword: string;
    candidates: MemberWithGroupContext[];
  } | null>(null);
  /** 대시보드 자체 저장 시 이벤트 핸들러 무시 플래그 */
  const selfSavingRef = useRef(false);
  /** 길게 클릭 게이지 상태: { itemId, progress } */
  const [holdGauge, setHoldGauge] = useState<{ itemId: string; progress: number } | null>(null);
  const holdStartRef = useRef<{ itemId: string; startTime: number } | null>(null);
  const holdRafRef = useRef<number | null>(null);
  const holdDelayTimerRef = useRef<number | null>(null);
  const holdCompletedRef = useRef(false);
  const skipTodoCardOpenRef = useRef(false);
  
  // 드래그 감지를 위한 ref들
  const pointerStartPositionRef = useRef<{ x: number; y: number; itemId: string } | null>(null);
  const isDraggingRef = useRef(false);

  const weekdayKey = useMemo(() => dateToWeekdayKey(viewDate), [viewDate]);
  const mondayIso = useMemo(() => mondayIsoForDateInLocal(viewDate), [viewDate]);
  const prevMondayIso = useMemo(() => shiftMondayIso(mondayIso, -1), [mondayIso]);
  const nextMondayIso = useMemo(() => shiftMondayIso(mondayIso, 1), [mondayIso]);

  const calendarTodayIso = useMemo(() => toISODate(new Date()), []);
  const isViewingToday = useMemo(() => toISODate(viewDate) === calendarTodayIso, [calendarTodayIso, viewDate]);

  /** 상단 '통합 클래스 보기' 또는 클래스 미생성/미일치: 대시보드는 반드시 낱 클래스 단위 */
  const hasClassForDashboard = useMemo(
    () =>
      Boolean(
        workspaceId?.trim() &&
          currentGradeId?.trim() &&
          grades.length > 0 &&
          grades.some((g) => g.id === currentGradeId)
      ),
    [workspaceId, currentGradeId, grades]
  );

  const todayIso = useMemo(() => toISODate(viewDate), [viewDate]);

  const {
    daySlots,
    loading: lessonLoading,
    error: lessonError,
    terms: lessonTerms,
    academicYear: lessonAcademicYear,
    activeTermNumber: lessonActiveTermNumber,
    lessonBundle,
  } = useDashboardLessonSectionData({
    workspaceId,
    gradeId: currentGradeId,
    grades,
    mondayIso,
    weekdayKey,
    dateIso: todayIso,
    enabled: hasClassForDashboard,
    refreshNonce: lessonRefreshNonce,
  });
  const calendarLinkedCtx = useCalendarLinkedContext(workspaceId, currentGradeId);
  const timetableLinkedCalendarDays = calendarLinkedCtx.useDbCalendarDays
    ? null
    : calendarLinkedCtx.localCalendarDays;

  const semesterWeekIndex = useMemo(
    () => resolveSemesterWeekIndex(lessonTerms, mondayIso, todayIso),
    [lessonTerms, mondayIso, todayIso]
  );
  /** 시간표 모드: 주간학습 표 로드 전에는 오늘 주 기준으로 표시 (깜박임 방지) */
  const timetableFallbackWeekIndex = useMemo(() => {
    const now = new Date();
    return resolveSemesterWeekIndex(lessonTerms, mondayIsoForDateInLocal(now), toISODate(now));
  }, [lessonTerms]);

  const [holidayCacheRev, setHolidayCacheRev] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const bump = () => setHolidayCacheRev((n) => n + 1);
    const onStorage = (e: StorageEvent) => {
      const k = e.key ?? '';
      if (
        k.includes('cp.academicCalendarSections.') ||
        k.includes('cp.academicSectionCalendarDays.') ||
        k.includes('cp.calendarDays.')
      ) {
        bump();
      }
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(ACADEMIC_SECTION_CALENDAR_CHANGE_EVENT, bump);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(ACADEMIC_SECTION_CALENDAR_CHANGE_EVENT, bump);
    };
  }, []);

  /** 월별/위클리 공휴일·휴업일: LS 캐시 + 데일리와 동일 RQ 번들(학기별 holidayDays) 병합 */
  const dashboardHolidayCache = useMemo(() => {
    const fromLs = collectAcademicCalendarHolidayCache(workspaceId, currentGradeId);
    const t1 = lessonBundle?.byTerm?.[1];
    const t2 = lessonBundle?.byTerm?.[2];
    return mergeOffDaysIntoHolidayCache(fromLs, [t1, t2]);
  }, [workspaceId, currentGradeId, holidayCacheRev, lessonRefreshNonce, lessonBundle]);
  const dashboardHolidayIsoSet = dashboardHolidayCache.isoSet;
  const dashboardHolidayNoteByIso = dashboardHolidayCache.noteByIso;
  const dailyDateLabel = formatDashboardWeekMmDd(todayIso);
  const dailyWeekdayKo = `${(['일', '월', '화', '수', '목', '금', '토'] as const)[viewDate.getDay()]}요일`;
  const dailyHolidayName = (dashboardHolidayNoteByIso[todayIso] ?? '').trim();
  const isDailyWeekend = viewDate.getDay() === 0 || viewDate.getDay() === 6;

  const dailyPeriodTimeBySlotIndex = useMemo(() => {
    const map = new Map<number, string>();
    const year = lessonBundle?.academicYear || lessonAcademicYear || new Date().getFullYear();
    const term =
      lessonActiveTermNumber === 1 || lessonActiveTermNumber === 2
        ? lessonActiveTermNumber
        : 1;
    const schedule = resolveClassScheduleSettingsFromLocalCaches({
      workspaceId,
      academicYear: year,
      termNumber: term,
      gradeIds: [currentGradeId],
    });
    if (!schedule) return map;
    const entry = resolveClassEntryByGradeId(schedule, grades, currentGradeId);
    if (!entry?.sectionId) return map;
    const weekdayForTimes = isWeekdayTimeKey(weekdayKey) ? weekdayKey : null;
    const rows = resolveClassScheduleRowsForWeekday(schedule, entry.sectionId, weekdayForTimes);
    rows.forEach((row, idx) => {
      const label = formatPeriodTimeRange(row.startTime, row.endTime);
      if (label) map.set(idx, label);
    });
    return map;
  }, [
    currentGradeId,
    grades,
    lessonAcademicYear,
    lessonActiveTermNumber,
    lessonBundle?.academicYear,
    weekdayKey,
    workspaceId,
    lessonRefreshNonce,
  ]);

  const plannerTermNumber = useMemo(() => {
    if (lessonActiveTermNumber === 1 || lessonActiveTermNumber === 2) return lessonActiveTermNumber;
    if (lessonTerms.length > 0) return resolveTermNumberForViewDate(lessonTerms, todayIso);
    return null;
  }, [lessonActiveTermNumber, lessonTerms, todayIso]);
  const isWeeklyLessonView = lessonViewMode === 'weekly';
  const isCalendarLessonView = lessonViewMode === 'calendar';
  const isTimetableLessonView = lessonViewMode === 'timetable';
  const isPlannerLessonView = isWeeklyLessonView || isCalendarLessonView || isTimetableLessonView;
  const weeklyDaysVertical = weeklyDaysAutoVertical || weeklyDaysVerticalPref;

  useEffect(() => {
    writeDashboardLessonViewMode(lessonViewMode);
  }, [lessonViewMode]);

  useEffect(() => {
    if (!isTimetableLessonView) setTimetableNavState(null);
  }, [isTimetableLessonView]);

  useEffect(() => {
    if (lessonViewMode !== 'weekly') setWeeklyNotesMode(false);
  }, [lessonViewMode]);

  useEffect(() => {
    writeDashboardWeeklyCalendarLayout(calendarLayoutMode);
  }, [calendarLayoutMode]);

  useEffect(() => {
    writeDashboardCalendarFixedEventsOnly(calendarFixedEventsOnly);
  }, [calendarFixedEventsOnly]);

  useEffect(() => {
    const apply = () => setIsMobileCalendarLayout(matchesMaxWidth(900));
    apply();
    window.addEventListener('resize', apply);
    window.addEventListener(PAGE_ZOOM_CHANGED_EVENT, apply);
    return () => {
      window.removeEventListener('resize', apply);
      window.removeEventListener(PAGE_ZOOM_CHANGED_EVENT, apply);
    };
  }, []);

  const weekDateIsos = useMemo(() => weekDateIsosFromMonday(mondayIso), [mondayIso]);

  useEffect(() => {
    if (!weeklyNotesMode || !userId || !mondayIso) return;
    let cancelled = false;
    const weekKey = `${userId}|${mondayIso}`;
    const sameWeekReload = loadedDailyNotesWeekKeyRef.current === weekKey;
    if (!sameWeekReload) {
      const cached = loadDailyRecordNotesWeekRowsCache(userId, mondayIso) ?? [];
      setDailyWeekNotesBaseline(cached);
      dailyNotesBaselineRef.current = cached;
      setDailyWeekNotes(overlayDailyRecordNotesWeekRowsDraft(userId, mondayIso, cached));
      loadedDailyNotesWeekKeyRef.current = weekKey;
    }
    void fetchDailyRecordNotesForWeek(userId, mondayIso, weekDateIsos)
      .then((payload) => {
        if (cancelled) return;
        setDailyWeekNotes((local) =>
          mergeWeekNotesKeepingLocalDirty(payload.notes, local, dailyNotesBaselineRef.current)
        );
        setDailyWeekNotesBaseline(payload.notes);
        dailyNotesBaselineRef.current = payload.notes;
      })
      .catch((e) => {
        if (cancelled) return;
        showDailyNotesError(e instanceof Error ? e.message : '일자별 기록을 불러오지 못했습니다.');
      });
    return () => {
      cancelled = true;
    };
  }, [weeklyNotesMode, userId, mondayIso, weekDateIsos, showDailyNotesError]);

  useEffect(() => {
    if (!weeklyNotesMode || !userId) return;
    const peeked = peekDailyNoteCategoriesCache(userId);
    if (peeked) setDailyNoteCategories(peeked);
    let cancelled = false;
    void fetchDailyNoteCategories(userId)
      .then((rows) => {
        if (!cancelled) setDailyNoteCategories(rows);
      })
      .catch(() => {
        /* 카테고리는 쪽지 입력에만 필요 — 실패해도 일반 기록은 유지 */
      });
    return () => {
      cancelled = true;
    };
  }, [weeklyNotesMode, userId]);

  const [adjacentNotesRev, setAdjacentNotesRev] = useState(0);
  const adjacentNotesByMonday = useMemo(() => {
    if (!userId) return { prev: [] as DailyWeekNoteDraft[], next: [] as DailyWeekNoteDraft[] };
    return {
      prev: overlayDailyRecordNotesWeekRowsDraft(
        userId,
        prevMondayIso,
        loadDailyRecordNotesWeekRowsCache(userId, prevMondayIso) ?? []
      ),
      next: overlayDailyRecordNotesWeekRowsDraft(
        userId,
        nextMondayIso,
        loadDailyRecordNotesWeekRowsCache(userId, nextMondayIso) ?? []
      ),
    };
  }, [userId, prevMondayIso, nextMondayIso, adjacentNotesRev, weeklyNotesMode]);

  useEffect(() => {
    if (!weeklyNotesMode || !userId) return undefined;
    let cancelled = false;
    void Promise.all([
      fetchDailyRecordNotesForWeek(userId, prevMondayIso, weekDateIsosFromMonday(prevMondayIso)),
      fetchDailyRecordNotesForWeek(userId, nextMondayIso, weekDateIsosFromMonday(nextMondayIso)),
    ])
      .then(() => {
        if (!cancelled) setAdjacentNotesRev((n) => n + 1);
      })
      .catch(() => {
        /* 인접 주 미리보기는 캐시가 없으면 빈 칸 */
      });
    return () => {
      cancelled = true;
    };
  }, [weeklyNotesMode, userId, prevMondayIso, nextMondayIso]);

  const dailyNotesDirty = useMemo(
    () => weekNotesSignature(dailyWeekNotes) !== weekNotesSignature(dailyWeekNotesBaseline),
    [dailyWeekNotes, dailyWeekNotesBaseline]
  );

  const handleWeekNotesChange = useCallback((next: DailyWeekNoteDraft[]) => {
    setDailyWeekNotes(next);
    if (userId && mondayIso) {
      persistDailyRecordNotesWeekRowsDraft(userId, mondayIso, next, dailyNotesBaselineRef.current);
    }
  }, [userId, mondayIso]);

  const closeWeeklyNoteUnlock = useCallback(() => {
    if (weeklyNoteUnlockBusy) return;
    setWeeklyNoteUnlockOpen(false);
    setWeeklyNoteUnlockError('');
  }, [weeklyNoteUnlockBusy]);

  const handleWeeklyNoteUnlockSubmit = useCallback(
    async (value: string) => {
      if (!userId || weeklyNoteUnlockBusy) return;
      setWeeklyNoteUnlockBusy(true);
      setWeeklyNoteUnlockError('');
      try {
        const lock = loadDailyNoteViewLock(userId);
        const ok = await dailyNoteLockPasswordMatches(userId, value.trim(), lock.passwordHash);
        if (!ok) {
          setWeeklyNoteUnlockError('잠금 비밀번호가 올바르지 않습니다.');
          return;
        }
        setDailyNoteViewSessionUnlocked(userId, true);
        setWeeklyNoteUnlockOpen(false);
        setWeeklyNoteUnlockError('');
      } catch (e) {
        setWeeklyNoteUnlockError(e instanceof Error ? e.message : '확인에 실패했습니다.');
      } finally {
        setWeeklyNoteUnlockBusy(false);
      }
    },
    [userId, weeklyNoteUnlockBusy]
  );

  const handleSaveDailyNotes = useCallback(async () => {
    if (!userId || !dailyNotesDirty || dailyNotesSaving) return;
    const defaultCatId = defaultDailyCategoryId(dailyNoteCategories);
    const toSave = prepareWeekNotesForSave(dailyWeekNotes, defaultCatId);
    setDailyNotesSaving(true);
    showDailyNotesSaving();
    try {
      const saved = await saveDailyRecordNotesWeek(
        userId,
        mondayIso,
        toSave,
        dailyWeekNotesBaseline
      );
      setDailyWeekNotes(saved);
      setDailyWeekNotesBaseline(saved);
      dailyNotesBaselineRef.current = saved;
      clearDailyRecordNotesWeekRowsDraft(userId, mondayIso);
      showDailyNotesSuccess('저장되었습니다.');
    } catch (e) {
      showDailyNotesError(e instanceof Error ? e.message : '저장에 실패했습니다.');
    } finally {
      setDailyNotesSaving(false);
    }
  }, [
    userId,
    dailyNotesDirty,
    dailyNotesSaving,
    dailyWeekNotes,
    dailyWeekNotesBaseline,
    dailyNoteCategories,
    mondayIso,
    showDailyNotesSaving,
    showDailyNotesSuccess,
    showDailyNotesError,
  ]);

  useEffect(() => {
    writeDashboardWeeklyDaysVerticalPref(weeklyDaysVerticalPref);
  }, [weeklyDaysVerticalPref]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(
      `(max-width: ${DASHBOARD_WEEKLY_DAYS_VERTICAL_MAX_WIDTH_PX}px)`
    );
    const sync = () => setWeeklyDaysAutoVertical(mq.matches);
    sync();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', sync);
      return () => mq.removeEventListener('change', sync);
    }
    mq.addListener(sync);
    return () => mq.removeListener(sync);
  }, []);
  const plannerAcademicYear = lessonAcademicYear || new Date().getFullYear();
  const { minMonday: plannerMinMonday, maxMonday: plannerMaxMonday } = useMemo(
    () => plannerMinMaxMondayIso(plannerAcademicYear),
    [plannerAcademicYear]
  );
  const canPrevPlannerWeek = mondayIso > plannerMinMonday;
  const canNextPlannerWeek = mondayIso < plannerMaxMonday;

  const goPlannerWeek = useCallback(
    (dir: -1 | 1) => {
      setViewDate((prev) => clampDateToPlannerWeeks(addDays(prev, dir * 7), plannerAcademicYear));
    },
    [plannerAcademicYear]
  );

  const calendarMinMonth = useMemo(() => {
    const { startIso } = dashboardPlannerBounds(plannerAcademicYear);
    const s = parseISODate(startIso);
    return new Date(s.getFullYear(), s.getMonth(), 1);
  }, [plannerAcademicYear]);
  const calendarMaxMonth = useMemo(() => {
    const { endIso } = dashboardPlannerBounds(plannerAcademicYear);
    const e = parseISODate(endIso);
    return new Date(e.getFullYear(), e.getMonth(), 1);
  }, [plannerAcademicYear]);
  const canPrevCalendarMonth = calendarMonth.getTime() > calendarMinMonth.getTime();
  const canNextCalendarMonth = calendarMonth.getTime() < calendarMaxMonth.getTime();

  const goCalendarMonth = useCallback(
    (dir: -1 | 1 | 0) => {
      setCalendarMonth((prev) => {
        const base =
          dir === 0 ? new Date() : new Date(prev.getFullYear(), prev.getMonth() + dir, 1);
        return clampMonthToPlanner(base, plannerAcademicYear);
      });
    },
    [plannerAcademicYear]
  );

  const calendarEnteredRef = useRef(false);
  useEffect(() => {
    if (!isCalendarLessonView) {
      calendarEnteredRef.current = false;
      return;
    }
    if (calendarEnteredRef.current) {
      setCalendarMonth((prev) => {
        const next = clampMonthToPlanner(prev, plannerAcademicYear);
        return next.getTime() === prev.getTime() ? prev : next;
      });
      return;
    }
    calendarEnteredRef.current = true;
    setCalendarMonth(
      clampMonthToPlanner(new Date(viewDate.getFullYear(), viewDate.getMonth(), 1), plannerAcademicYear)
    );
  }, [isCalendarLessonView, plannerAcademicYear, viewDate]);

  const weekSwipe = useDashboardWeeklyWeekSwipe({
    enabled: isWeeklyLessonView,
    mondayIso,
    canPrev: canPrevPlannerWeek,
    canNext: canNextPlannerWeek,
    onPrevWeek: () => goPlannerWeek(-1),
    onNextWeek: () => goPlannerWeek(1),
  });

  useEffect(() => {
    if (!isWeeklyLessonView) return;
    setViewDate((prev) => {
      const next = clampDateToPlannerWeeks(prev, plannerAcademicYear);
      return mondayIsoForDateInLocal(next) === mondayIsoForDateInLocal(prev) ? prev : next;
    });
  }, [isWeeklyLessonView, plannerAcademicYear]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (
        e.key !== 'ArrowLeft' &&
        e.key !== 'ArrowRight' &&
        e.key !== 'ArrowUp' &&
        e.key !== 'ArrowDown'
      ) {
        return;
      }
      if (shouldIgnoreAppNavigationShortcut(e)) return;
      if (typeof window !== 'undefined' && window.location.pathname !== '/dashboard') return;
      if (isTimetableLessonView) return;
      if (
        todoAddModalOpen ||
        todoEditModalOpen ||
        todoRoutineModalOpen ||
        yearCalendarModalOpen ||
        lessonRecordModalOpen ||
        behaviorNoteModalOpen ||
        weeklyNoteUnlockOpen ||
        dailyLessonMenu
      ) {
        return;
      }
      if (
        document.querySelector(
          '.cp-modal-backdrop, .cp-pin-confirm-overlay, .cp-dash-weekly-lesson-menu'
        )
      ) {
        return;
      }

      if (isCalendarLessonView) {
        if (e.key === 'ArrowLeft' && canPrevCalendarMonth) {
          e.preventDefault();
          goCalendarMonth(-1);
        } else if (e.key === 'ArrowRight' && canNextCalendarMonth) {
          e.preventDefault();
          goCalendarMonth(1);
        } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
          goCalendarMonth(0);
        }
        return;
      }

      if (isWeeklyLessonView) {
        if (e.key === 'ArrowLeft' && canPrevPlannerWeek) {
          e.preventDefault();
          goPlannerWeek(-1);
        } else if (e.key === 'ArrowRight' && canNextPlannerWeek) {
          e.preventDefault();
          goPlannerWeek(1);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setWeeklyNotesMode(false);
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          setWeeklyNotesMode(true);
        }
        return;
      }

      // 데일리: ←/→ 날짜 이동, ↑/↓ 오늘로 이동
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setViewDate((prev) => addDaysForDashboardNav(prev, -1));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setViewDate((prev) => addDaysForDashboardNav(prev, 1));
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        setViewDate(new Date());
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    behaviorNoteModalOpen,
    canNextCalendarMonth,
    canNextPlannerWeek,
    canPrevCalendarMonth,
    canPrevPlannerWeek,
    dailyLessonMenu,
    goCalendarMonth,
    goPlannerWeek,
    isCalendarLessonView,
    isTimetableLessonView,
    isWeeklyLessonView,
    lessonRecordModalOpen,
    todoAddModalOpen,
    todoEditModalOpen,
    todoRoutineModalOpen,
    weeklyNoteUnlockOpen,
    yearCalendarModalOpen,
  ]);

  const bumpLessonRefresh = useCallback(() => {
    setLessonRefreshNonce((n) => n + 1);
  }, []);

  /** keep-alive: 연간진도·프로그램 저장 후 수업 섹션 재로드 */
  useEffect(() => {
    if (!hasClassForDashboard || !workspaceId?.trim() || !currentGradeId?.trim()) return undefined;

    const onProgramSaved = (e: Event) => {
      if (selfSavingRef.current) return;
      const d = (e as CustomEvent<ProgramProgressSavedDetail>).detail;
      if (!d?.workspaceId || d.workspaceId !== workspaceId || d.gradeId !== currentGradeId) return;
      bumpLessonRefresh();
    };

    const onTimetableSaved = (e: Event) => {
      const d = (e as CustomEvent<AnnualTimetableSavedDetail>).detail;
      if (!d?.workspaceId || d.workspaceId !== workspaceId) return;
      bumpLessonRefresh();
    };

    window.addEventListener(PROGRAM_PROGRESS_SAVED_EVENT, onProgramSaved as EventListener);
    window.addEventListener(ANNUAL_TIMETABLE_SAVED_EVENT, onTimetableSaved as EventListener);
    return () => {
      window.removeEventListener(PROGRAM_PROGRESS_SAVED_EVENT, onProgramSaved as EventListener);
      window.removeEventListener(ANNUAL_TIMETABLE_SAVED_EVENT, onTimetableSaved as EventListener);
    };
  }, [bumpLessonRefresh, currentGradeId, hasClassForDashboard, workspaceId]);

  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => {
      setUserId(data.user?.id ?? '');
    });
  }, []);

  const noteWorkspaceId = workspaceId?.trim() ? workspaceId.trim() : null;
  const todoWorkspaceQuery = useTodoNoteWorkspaceQuery({
    userId,
    noteWorkspaceId,
    enabled: !!userId && !!noteWorkspaceId,
  });
  const selectedTodoBoard = useMemo(
    () => todoBoards.find((b) => b.id === todoSelectedBoardId) ?? null,
    [todoBoards, todoSelectedBoardId]
  );
  const selectedTodoBoardIsDefault = selectedTodoBoard
    ? isTodoShareDefaultBoard(selectedTodoBoard)
    : true;
  const todoItems = selectedTodoBoardIsDefault
    ? (todoWorkspaceQuery.data?.items ?? [])
    : (todoAltSnapshot?.items ?? []);
  const todoCategories = selectedTodoBoardIsDefault
    ? (todoWorkspaceQuery.data?.categories ?? [])
    : (todoAltSnapshot?.categories ?? []);
  const todoLoading =
    !todoSelectedBoardId ||
    (selectedTodoBoardIsDefault ? todoWorkspaceQuery.isLoading : todoAltLoading);
  const todoError = selectedTodoBoardIsDefault
    ? todoWorkspaceQuery.error instanceof Error
      ? todoWorkspaceQuery.error.message
      : todoWorkspaceQuery.error
        ? 'ToDo 노트를 불러오지 못했습니다.'
        : null
    : todoAltError;

  useEffect(() => {
    setTodoSelectedBoardId(null);
    setTodoAltSnapshot(null);
    setTodoBoards([]);
  }, [noteWorkspaceId]);

  useEffect(() => {
    if (!userId || !noteWorkspaceId) return undefined;
    let cancelled = false;
    void (async () => {
      try {
        await migratePersonalTodoToDefaultBoard(noteWorkspaceId);
        const rows = sortTodoShareBoards(await listTodoShareBoards(noteWorkspaceId));
        if (cancelled) return;
        setTodoBoards(rows);
        const pref = loadTodoNoteSelectedBoardPreference(noteWorkspaceId, userId);
        const nextId = resolveTodoNoteSelectedBoardId(rows, pref);
        setTodoSelectedBoardId((prev) => prev ?? nextId);
      } catch {
        if (!cancelled) setTodoBoards([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, noteWorkspaceId]);

  const persistDashboardBoard = useCallback(
    (board: { id: string; title?: string | null }) => {
      if (!noteWorkspaceId) return;
      saveTodoNoteSelectedBoardPreference(noteWorkspaceId, {
        id: board.id,
        name: todoShareNameFromTitle(board.title ?? ''),
      });
    },
    [noteWorkspaceId]
  );

  const selectDashboardTodoBoard = useCallback(
    (boardId: string, opts?: { fromRemote?: boolean }) => {
      if (!boardId || boardId === todoSelectedBoardId) return;
      const board = todoBoards.find((b) => b.id === boardId);
      setTodoSelectedBoardId(boardId);
      if (board) persistDashboardBoard(board);
      else persistDashboardBoard({ id: boardId, title: '' });
      if (!opts?.fromRemote) {
        emitTodoNoteBoardSelection({
          noteWorkspaceId,
          boardId,
          source: 'dashboard',
        });
      }
    },
    [todoSelectedBoardId, todoBoards, persistDashboardBoard, noteWorkspaceId]
  );

  useEffect(() => {
    const onSel = (e: Event) => {
      const d = (e as CustomEvent<TodoNoteBoardSelectionDetail>).detail;
      if (!d?.boardId || d.source === 'dashboard') return;
      const detailWs = d.noteWorkspaceId?.trim() ? d.noteWorkspaceId.trim() : null;
      if (detailWs !== noteWorkspaceId) return;
      selectDashboardTodoBoard(d.boardId, { fromRemote: true });
    };
    window.addEventListener(TODO_NOTE_BOARD_SELECTION_EVENT, onSel as EventListener);
    return () => window.removeEventListener(TODO_NOTE_BOARD_SELECTION_EVENT, onSel as EventListener);
  }, [noteWorkspaceId, selectDashboardTodoBoard]);

  useEffect(() => {
    if (!todoSelectedBoardId || selectedTodoBoardIsDefault) {
      setTodoAltSnapshot(null);
      setTodoAltError(null);
      setTodoAltLoading(false);
      return;
    }
    const boardId = todoSelectedBoardId;
    const board = selectedTodoBoard;
    if (!board) return;
    let cancelled = false;
    const cached = loadTodoShareBoardContentCache(boardId);
    if (cached && cached.revision === board.revision) {
      setTodoAltSnapshot({ categories: cached.categories, items: cached.items });
      setTodoAltError(null);
      setTodoAltLoading(false);
      return;
    }
    setTodoAltLoading(true);
    setTodoAltError(null);
    void loadTodoShareAsOwner(boardId)
      .then((snap) => {
        if (cancelled) return;
        const next = { categories: snap.categories, items: snap.items };
        setTodoAltSnapshot(next);
        saveTodoShareBoardContentCache(boardId, {
          title: snap.title,
          revision: snap.revision,
          public_token: snap.public_token ?? null,
          is_active: snap.is_active ?? null,
          categories: snap.categories,
          items: snap.items,
          postit_layout: snap.postit_layout ?? {},
        });
        setTodoBoards((prev) =>
          prev.map((b) => (b.id === boardId ? { ...b, revision: snap.revision } : b))
        );
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setTodoAltError(e instanceof Error ? e.message : 'ToDo 노트를 불러오지 못했습니다.');
      })
      .finally(() => {
        if (!cancelled) setTodoAltLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [todoSelectedBoardId, selectedTodoBoardIsDefault, selectedTodoBoard]);

  /** keep-alive: Todo 노트 저장 시 추가 fetch 없이 섹션 반영 */
  useEffect(() => {
    if (!userId || !noteWorkspaceId) return undefined;
    const onTodoSaved = (e: Event) => {
      const d = (e as CustomEvent<TodoNoteWorkspaceSavedDetail>).detail;
      if (!d?.userId || d.userId !== userId || !d.snapshot) return;
      const detailWs = d.noteWorkspaceId?.trim() ? d.noteWorkspaceId.trim() : null;
      if (detailWs !== noteWorkspaceId) return;
      const eventBoardId = d.boardId?.trim() || '';
      if (eventBoardId && todoSelectedBoardId && eventBoardId !== todoSelectedBoardId) return;
      const treatAsDefault = !eventBoardId || isTodoShareDefaultBoard(selectedTodoBoard ?? { title: '' });
      if (treatAsDefault) {
        setTodoNoteWorkspaceQueryData(userId, noteWorkspaceId, d.snapshot);
      } else {
        setTodoAltSnapshot(d.snapshot);
      }
    };
    window.addEventListener(TODO_NOTE_WORKSPACE_SAVED_EVENT, onTodoSaved as EventListener);
    return () => {
      window.removeEventListener(TODO_NOTE_WORKSPACE_SAVED_EVENT, onTodoSaved as EventListener);
    };
  }, [userId, noteWorkspaceId, todoSelectedBoardId, selectedTodoBoard]);

  useEffect(() => {
    if (!workspaceId?.trim()) {
      setTodoExpanded(false);
    }
  }, [workspaceId]);

  const canShowMain = hasClassForDashboard;
  const showWeekendHint = hasClassForDashboard && weekdayKey == null;
  const lessonCachePending = hasClassForDashboard && weekdayKey != null && lessonLoading;
  const showSlotGrid =
    hasClassForDashboard && weekdayKey != null && !lessonCachePending && daySlots.length > 0;
  const showNoScheduleHint =
    hasClassForDashboard &&
    weekdayKey != null &&
    !lessonCachePending &&
    daySlots.length === 0 &&
    !lessonError;

  const dashboardTodoRowsTwoWeek = useMemo(() => {
    const categoryById = new Map(todoCategories.map((c) => [c.id, c]));
    return listDashboardDailyIncompleteTodos(todoItems, toISODate(new Date()), 'twoWeek').map((item) =>
      mapTodoDashboardRow(item, categoryById)
    );
  }, [todoCategories, todoItems]);

  const dashboardTodoRowsAllIncomplete = useMemo(() => {
    const categoryById = new Map(todoCategories.map((c) => [c.id, c]));
    return listDashboardDailyIncompleteTodos(todoItems, toISODate(new Date()), 'all').map((item) =>
      mapTodoDashboardRow(item, categoryById)
    );
  }, [todoCategories, todoItems]);

  const dashboardTodoRows = todoExpanded ? dashboardTodoRowsAllIncomplete : dashboardTodoRowsTwoWeek;

  const dashboardTodoTiles = useMemo(() => {
    const items = dashboardTodoRows.map((r) => r.item);
    return buildTodoPostitTiles(items, calendarTodayIso);
  }, [dashboardTodoRows, calendarTodayIso]);

  /**
   * 위클리 수업: DB를 요일마다 치지 않음.
   * 대시보드 번들 + 연간진도 LS(시간표·진도·scoped)를 합쳐 단원·학습내용까지 표시.
   */
  const weeklySlotsByDateIso = useMemo(() => {
    const map: Record<string, DashboardAnnualDaySlot[]> = {};
    if (!hasClassForDashboard || !workspaceId?.trim() || !currentGradeId?.trim()) return map;
    const weekMondays = [prevMondayIso, mondayIso, nextMondayIso];
    const termsForResolve = lessonBundle?.terms?.length ? lessonBundle.terms : lessonTerms;
    const year = lessonBundle?.academicYear || lessonAcademicYear || new Date().getFullYear();
    for (const weekMonday of weekMondays) {
      const dates = weekDateIsosFromMonday(weekMonday);
      for (const iso of dates) {
        const d = parseISODate(iso);
        const wk = dateToWeekdayKey(d);
        if (!wk) {
          map[iso] = [];
          continue;
        }
        const tn = resolveTermNumberForViewDate(termsForResolve, iso);
        map[iso] = buildDashboardWeeklyDaySlots({
          workspaceId,
          gradeId: currentGradeId,
          grades,
          mondayIso: weekMonday,
          weekdayKey: wk,
          dateIso: iso,
          academicYear: year,
          termNumber: tn,
          bundle: lessonBundle,
        });
      }
    }
    return map;
  }, [
    currentGradeId,
    grades,
    hasClassForDashboard,
    lessonAcademicYear,
    lessonBundle,
    lessonRefreshNonce,
    lessonTerms,
    mondayIso,
    nextMondayIso,
    prevMondayIso,
    daySlots,
    workspaceId,
  ]);

  /** 위클리 교시 시각: `dateIso:slotIndex` → 시작·종료 (데일리와 같은 학급 시간표 LS) */
  const weeklyPeriodTimeByKey = useMemo(() => {
    const map: Record<string, { start: string; end: string }> = {};
    if (!workspaceId?.trim() || !currentGradeId?.trim()) return map;
    const termsForResolve = lessonBundle?.terms?.length ? lessonBundle.terms : lessonTerms;
    const year = lessonBundle?.academicYear || lessonAcademicYear || new Date().getFullYear();
    const scheduleByTerm = new Map<number, ReturnType<typeof resolveClassScheduleSettingsFromLocalCaches>>();
    for (const weekMonday of [prevMondayIso, mondayIso, nextMondayIso]) {
      for (const iso of weekDateIsosFromMonday(weekMonday)) {
        const wk = dateToWeekdayKey(parseISODate(iso));
        if (!wk || !isWeekdayTimeKey(wk)) continue;
        const resolvedTerm = resolveTermNumberForViewDate(termsForResolve, iso);
        const term = resolvedTerm === 1 || resolvedTerm === 2 ? resolvedTerm : 1;
        if (!scheduleByTerm.has(term)) {
          scheduleByTerm.set(
            term,
            resolveClassScheduleSettingsFromLocalCaches({
              workspaceId,
              academicYear: year,
              termNumber: term,
              gradeIds: [currentGradeId],
            })
          );
        }
        const schedule = scheduleByTerm.get(term);
        if (!schedule) continue;
        const entry = resolveClassEntryByGradeId(schedule, grades, currentGradeId);
        if (!entry?.sectionId) continue;
        resolveClassScheduleRowsForWeekday(schedule, entry.sectionId, wk).forEach((row, idx) => {
          const start = (row.startTime ?? '').trim();
          const end = (row.endTime ?? '').trim();
          if (start || end) map[weeklyBehaviorRecordKey(iso, idx)] = { start, end };
        });
      }
    }
    return map;
  }, [
    currentGradeId,
    grades,
    lessonAcademicYear,
    lessonBundle?.academicYear,
    lessonBundle?.terms,
    lessonRefreshNonce,
    lessonTerms,
    mondayIso,
    nextMondayIso,
    prevMondayIso,
    workspaceId,
  ]);

  /** 위클리: 마감일이 해당 날짜인 TODO(완료 포함 · Todo 노트 LS/RQ 캐시) */
  const weeklyTodosByDateIso = useMemo(() => {
    const map: Record<string, { item: TodoNoteItemView; category: TodoNoteCategoryDraft | null }[]> = {};
    if (!todoItems.length) return map;
    const categoryById = new Map(todoCategories.map((c) => [c.id, c]));
    const dates = new Set([
      ...weekDateIsosFromMonday(prevMondayIso),
      ...weekDateIsosFromMonday(mondayIso),
      ...weekDateIsosFromMonday(nextMondayIso),
      ...(timetableMondayIso ? weekDateIsosFromMonday(timetableMondayIso) : []),
    ]);
    const displayed = expandTodoItemsForView(todoItems, toISODate(new Date()));
    for (const item of displayed) {
      const dueIso = String(item.due_date ?? '').trim();
      if (!dueIso) continue;
      let targetIso = '';
      if (item.due_kind === 'month') {
        const mk = dueIso.slice(0, 7);
        targetIso = `${mk}-01`;
      } else {
        targetIso = dueIso.slice(0, 10);
      }
      if (!dates.has(targetIso)) continue;
      const row = mapTodoDashboardRow(item, categoryById);
      const list = map[targetIso] ?? [];
      list.push(row);
      map[targetIso] = list;
    }
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => {
        const ac = a.item.completed ? 1 : 0;
        const bc = b.item.completed ? 1 : 0;
        if (ac !== bc) return ac - bc;
        return compareTodoItemsForSort(a.item, b.item);
      });
    }
    return map;
  }, [mondayIso, nextMondayIso, prevMondayIso, timetableMondayIso, todoCategories, todoItems]);

  const calendarCells = useMemo(
    () => buildDashboardMonthCells(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1),
    [calendarMonth]
  );

  const calendarSlotsByDateIso = useMemo(() => {
    const map: Record<string, DashboardAnnualDaySlot[]> = {};
    if (!isCalendarLessonView || !hasClassForDashboard || !workspaceId?.trim() || !currentGradeId?.trim()) {
      return map;
    }
    const termsForResolve = lessonBundle?.terms?.length ? lessonBundle.terms : lessonTerms;
    const year = lessonBundle?.academicYear || lessonAcademicYear || new Date().getFullYear();
    for (const cell of calendarCells) {
      const iso = cell.iso;
      if (!iso) continue;
      const d = parseISODate(iso);
      const wk = dateToWeekdayKey(d);
      if (!wk) {
        map[iso] = [];
        continue;
      }
      const tn = resolveTermNumberForViewDate(termsForResolve, iso);
      map[iso] = buildDashboardWeeklyDaySlots({
        workspaceId,
        gradeId: currentGradeId,
        grades,
        mondayIso: mondayIsoForDateInLocal(d),
        weekdayKey: wk,
        dateIso: iso,
        academicYear: year,
        termNumber: tn,
        bundle: lessonBundle,
      });
    }
    return map;
  }, [
    calendarCells,
    currentGradeId,
    grades,
    hasClassForDashboard,
    isCalendarLessonView,
    lessonAcademicYear,
    lessonBundle,
    lessonRefreshNonce,
    lessonTerms,
    daySlots,
    workspaceId,
  ]);

  const calendarTodosByDateIso = useMemo(() => {
    const map: Record<
      string,
      { item: TodoNoteItemView; category: TodoNoteCategoryDraft | null }[]
    > = {};
    if (!todoItems.length) return map;
    const categoryById = new Map(todoCategories.map((c) => [c.id, c]));
    const displayed = expandTodoItemsForView(todoItems, toISODate(new Date()));
    for (const item of displayed) {
      const dueIso = String(item.due_date ?? '').trim();
      if (!dueIso) continue;
      const targetIso =
        item.due_kind === 'month' ? `${dueIso.slice(0, 7)}-01` : dueIso.slice(0, 10);
      if (!targetIso) continue;
      const list = map[targetIso] ?? [];
      list.push({
        item,
        category: item.category_id ? categoryById.get(item.category_id) ?? null : null,
      });
      map[targetIso] = list;
    }
    return map;
  }, [todoCategories, todoItems]);

  /**
   * 데일리·위클리·캘린더에 보이는 날짜의 학기 연간진도표 캐시가 없으면
   * 연간진도표와 같은 RQ 키로 만들어 이후 화면이 재사용한다.
   */
  const dashboardLessonWarmedTermsRef = useRef(new Set<string>());
  useEffect(() => {
    dashboardLessonWarmedTermsRef.current.clear();
  }, [workspaceId, currentGradeId]);
  useEffect(() => {
    if (!hasClassForDashboard) return;
    if (!workspaceId?.trim() || !currentGradeId?.trim() || !lessonAcademicYear) return;
    const year = lessonAcademicYear;
    const termsForResolve = lessonBundle?.terms?.length ? lessonBundle.terms : lessonTerms;
    const dateIsos: string[] = [];
    if (isCalendarLessonView) {
      for (const cell of calendarCells) {
        if (cell.iso) dateIsos.push(cell.iso);
      }
    } else if (isWeeklyLessonView) {
      for (const weekMonday of [prevMondayIso, mondayIso, nextMondayIso]) {
        dateIsos.push(...weekDateIsosFromMonday(weekMonday));
      }
    } else {
      dateIsos.push(todayIso);
    }
    const termNumbers = collectDashboardVisibleLessonTermNumbers(termsForResolve, dateIsos);
    let cancelled = false;
    void (async () => {
      let didFetch = false;
      for (const tn of termNumbers) {
        const key = `${workspaceId}|${currentGradeId}|${year}|${tn}`;
        if (dashboardLessonWarmedTermsRef.current.has(key)) continue;
        if (!dashboardLessonTermContentNeedsWarm(workspaceId, year, tn, currentGradeId)) {
          dashboardLessonWarmedTermsRef.current.add(key);
          continue;
        }
        try {
          const fetched = await warmDashboardLessonTermContent(workspaceId, year, tn, currentGradeId);
          dashboardLessonWarmedTermsRef.current.add(key);
          if (fetched) didFetch = true;
        } catch {
          /* 실패하면 키를 남기지 않아 다음에 다시 만든다 */
        }
      }
      if (didFetch && !cancelled) bumpLessonRefresh();
    })();
    return () => {
      cancelled = true;
    };
  }, [
    bumpLessonRefresh,
    calendarCells,
    currentGradeId,
    hasClassForDashboard,
    isCalendarLessonView,
    isWeeklyLessonView,
    lessonAcademicYear,
    lessonBundle,
    lessonTerms,
    mondayIso,
    nextMondayIso,
    prevMondayIso,
    todayIso,
    workspaceId,
  ]);

  /** 위클리 진입 시 Todo LS 미스면 Todo 노트와 동일 fetch로 캐시 생성 */
  const weeklyTodoWarmKeyRef = useRef('');
  useEffect(() => {
    if (!isPlannerLessonView || !userId || !workspaceId?.trim()) return;
    const ws = workspaceId.trim();
    const warmKey = `${userId}|${ws}`;
    const ls = loadTodoNoteWorkspaceCache(userId, ws);
    if (ls.found) {
      weeklyTodoWarmKeyRef.current = warmKey;
      return;
    }
    if (todoWorkspaceQuery.isFetching || todoWorkspaceQuery.isLoading) return;
    if (weeklyTodoWarmKeyRef.current === warmKey) return;
    weeklyTodoWarmKeyRef.current = warmKey;
    void todoWorkspaceQuery.refetch();
  }, [isPlannerLessonView, todoWorkspaceQuery, userId, workspaceId]);

  const showTodoLoadMore = !todoExpanded && dashboardTodoRowsAllIncomplete.length > 0;

  const defaultCategory = useMemo(
    () => todoCategories.find((c) => c.name.trim() === '일반') ?? todoCategories[0] ?? null,
    [todoCategories]
  );

  const persistDashboardTodos = useCallback(
    async (params: {
      items: TodoNoteItemDraft[];
      categories?: TodoNoteCategoryDraft[];
      deletedItemIds?: string[];
    }) => {
      if (!noteWorkspaceId || !userId) return;
      const cats = params.categories ?? todoCategories;
      const deletedItemIds = params.deletedItemIds ?? [];
      const keptItems = deletedItemIds.length
        ? params.items.filter((it) => !deletedItemIds.includes(it.id))
        : params.items;
      if (!selectedTodoBoard || selectedTodoBoardIsDefault) {
        await saveTodoNoteWorkspace({
          noteWorkspaceId,
          categories: cats,
          items: keptItems,
          deletedCategoryIds: [],
          deletedItemIds,
        });
        return;
      }
      const newRev = await saveTodoShareAsOwner({
        boardId: selectedTodoBoard.id,
        expectedRevision: selectedTodoBoard.revision,
        payload: buildTodoShareSavePayload({
          categories: cats,
          items: keptItems,
          deletedCategoryIds: [],
          deletedItemIds,
        }),
      });
      setTodoBoards((prev) =>
        prev.map((b) => (b.id === selectedTodoBoard.id ? { ...b, revision: newRev } : b))
      );
      setTodoAltSnapshot({ categories: cats, items: keptItems });
      const cached = loadTodoShareBoardContentCache(selectedTodoBoard.id);
      saveTodoShareBoardContentCache(selectedTodoBoard.id, {
        title: cached?.title ?? selectedTodoBoard.title,
        revision: newRev,
        public_token: cached?.public_token ?? selectedTodoBoard.public_token,
        is_active: cached?.is_active ?? selectedTodoBoard.is_active,
        categories: cats,
        items: keptItems,
        postit_layout: cached?.postit_layout,
      });
      emitTodoNoteWorkspaceSaved({
        userId,
        noteWorkspaceId,
        snapshot: { categories: cats, items: keptItems },
        boardId: selectedTodoBoard.id,
      });
    },
    [noteWorkspaceId, userId, todoCategories, selectedTodoBoard, selectedTodoBoardIsDefault]
  );

  const handleToggleTodoComplete = useCallback(async (itemId: string) => {
    if (!workspaceId) return;
    const occ = parseRoutineOccurrenceId(itemId);
    let updatedItems: TodoNoteItemDraft[];
    if (occ) {
      const parent = todoItems.find((it) => it.id === occ.parentId);
      if (!parent) return;
      const todayIso = toISODate(new Date());
      updatedItems = todoItems.map((it) =>
        it.id === occ.parentId ? toggleRoutineOccurrenceComplete(it, occ.ymd, todayIso) : it
      );
    } else {
      const item = todoItems.find((it) => it.id === itemId);
      if (!item || item.is_routine) return;
      updatedItems = todoItems.map((it) =>
        it.id === itemId ? { ...it, completed: !it.completed } : it
      );
    }
    try {
      await persistDashboardTodos({ items: updatedItems });
    } catch {
      // ignore
    }
  }, [workspaceId, todoItems, persistDashboardTodos]);

  const handleToggleTodoFavorite = useCallback(
    async (itemId: string) => {
      if (!workspaceId) return;
      const persistId = parseRoutineOccurrenceId(itemId)?.parentId ?? itemId;
      const item = todoItems.find((it) => it.id === persistId);
      if (!item) return;
      const updatedItems = todoItems.map((it) =>
        it.id === persistId ? { ...it, is_favorite: !it.is_favorite } : it
      );
      try {
        await persistDashboardTodos({ items: updatedItems });
      } catch {
        // ignore
      }
    },
    [workspaceId, todoItems, persistDashboardTodos]
  );

  const handleDuplicateTodo = useCallback(
    (itemId: string) => {
      if (!workspaceId || !userId) return;
      if (parseRoutineOccurrenceId(itemId)) return;
      const anchor = todoItems.find((it) => it.id === itemId);
      if (!anchor || anchor.is_routine) return;
      const cat = anchor.category_id ? todoCategories.find((c) => c.id === anchor.category_id) : null;
      setTodoAddCategoryInput(cat?.name ?? defaultCategory?.name ?? DEFAULT_TODO_CATEGORY_NAME);
      setTodoAddIsDuplicate(true);
      setTodoAddDraft({
        ...anchor,
        id: `__add-draft-${crypto.randomUUID()}`,
        user_id: userId,
        workspace_id: workspaceId,
        completed: false,
        is_favorite: false,
      });
      setTodoAddModalOpen(true);
    },
    [workspaceId, userId, todoItems, todoCategories, defaultCategory?.name]
  );

  const cancelHoldGauge = useCallback(() => {
    if (holdDelayTimerRef.current != null) {
      window.clearTimeout(holdDelayTimerRef.current);
      holdDelayTimerRef.current = null;
    }
    if (holdRafRef.current != null) {
      cancelAnimationFrame(holdRafRef.current);
      holdRafRef.current = null;
    }
    holdStartRef.current = null;
    setHoldGauge(null);
  }, []);

  const runHoldGaugeLoop = useCallback(
    (itemId: string) => {
      const loop = () => {
        if (!holdStartRef.current || holdStartRef.current.itemId !== itemId) return;
        const elapsed = performance.now() - holdStartRef.current.startTime;
        const progress = Math.min(1, elapsed / TODO_COMPLETE_HOLD_MS);
        setHoldGauge({ itemId, progress });
        if (progress >= 1) {
          holdStartRef.current = null;
          setHoldGauge(null);
          holdCompletedRef.current = true;
          void handleToggleTodoComplete(itemId);
          setSelectedTodoItemId((prev) => (prev === itemId ? null : prev));
          return;
        }
        holdRafRef.current = requestAnimationFrame(loop);
      };
      holdRafRef.current = requestAnimationFrame(loop);
    },
    [handleToggleTodoComplete]
  );

  const scheduleHoldGauge = useCallback(
    (itemId: string) => {
      cancelHoldGauge();
      holdCompletedRef.current = false;
      holdDelayTimerRef.current = window.setTimeout(() => {
        holdDelayTimerRef.current = null;
        holdStartRef.current = { itemId, startTime: performance.now() };
        runHoldGaugeLoop(itemId);
      }, TODO_COMPLETE_HOLD_DELAY_MS);
    },
    [cancelHoldGauge, runHoldGaugeLoop]
  );

  const releaseTodoItemPointer = useCallback(
    (itemId: string, el: HTMLDivElement, pointerId: number) => {
      if (el.hasPointerCapture(pointerId)) {
        try {
          el.releasePointerCapture(pointerId);
        } catch {
          /* noop */
        }
      }
      cancelHoldGauge();
    },
    [cancelHoldGauge]
  );

  useEffect(() => {
    if (!selectedTodoItemId || todoEditModalOpen) return;
    if (!todoItems.some((it) => it.id === selectedTodoItemId && !it.completed)) {
      setSelectedTodoItemId(null);
    }
  }, [selectedTodoItemId, todoItems, todoEditModalOpen]);

  const openTodoAddModal = useCallback(async (dueDateIso?: string) => {
    if (!workspaceId) return;
    const { data: userData } = await supabase.auth.getUser();
    const uid = userData.user?.id;
    if (!uid) return;
    const dueIso = (dueDateIso ?? '').trim() || toISODate(new Date());
    setTodoAddCategoryInput(defaultCategory?.name ?? DEFAULT_TODO_CATEGORY_NAME);
    setTodoAddIsDuplicate(false);
    setTodoAddDraft({
      id: `__add-draft-${crypto.randomUUID()}`,
      user_id: uid,
      workspace_id: workspaceId,
      title: '',
      body: '',
      misc: '',
      due_kind: 'day',
      due_date: dueIso,
      category_id: defaultCategory?.id ?? null,
      completed: false,
      bucket_order: 0,
    });
    setTodoAddModalOpen(true);
  }, [defaultCategory?.id, defaultCategory?.name, workspaceId]);

  const closeTodoAddModal = useCallback(() => {
    setTodoAddModalOpen(false);
    setTodoAddIsDuplicate(false);
    setTodoAddDraft(null);
    setTodoAddCategoryInput('');
  }, []);

  const patchTodoAddDraft = useCallback((patch: Partial<TodoNoteItemDraft>) => {
    setTodoAddDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const closeTodoEditModal = useCallback(() => {
    setTodoEditModalOpen(false);
    setTodoEditDraft(null);
    setTodoEditCategoryInput('');
  }, []);

  const closeTodoRoutineModal = useCallback(() => {
    setTodoRoutineModalOpen(false);
    setTodoRoutineDraft(null);
    setTodoRoutineCategoryInput('');
    setTodoRoutineOccurrenceYmd(null);
    setTodoRoutineCompleteMark(null);
    setTodoRoutineCompleteTouched(false);
  }, []);

  const patchTodoEditDraft = useCallback((patch: Partial<TodoNoteItemDraft>) => {
    setTodoEditDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const openTodoEditModal = useCallback((itemId?: string) => {
    const id = itemId ?? selectedTodoItemId;
    if (!id) return;
    const occ = parseRoutineOccurrenceId(id);
    const parentId = occ?.parentId ?? id;
    const item = todoItems.find((it) => it.id === parentId);
    if (!item) return;
    const cat = item.category_id ? todoCategories.find((c) => c.id === item.category_id) : null;
    setSelectedTodoItemId(item.id);
    const catInput = cat?.name ?? DEFAULT_TODO_CATEGORY_NAME;
    if (item.is_routine && item.routine) {
      const displayed = expandTodoItemsForView(todoItems, toISODate(new Date()));
      const view = displayed.find((it) => it.id === id);
      const ymd =
        occ?.ymd ??
        (view?.due_kind === 'day' ? String(view.due_date ?? '').trim().slice(0, 10) : null) ??
        null;
      setTodoRoutineCategoryInput(catInput);
      setTodoRoutineDraft({
        ...item,
        due_kind: 'day',
        due_date: ymd || item.due_date,
        completed: view?.completed ?? false,
      });
      setTodoRoutineOccurrenceYmd(ymd && /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? ymd : null);
      setTodoRoutineCompleteMark(view?.completeMark ?? (view?.completed ? 'v' : null));
      setTodoRoutineCompleteTouched(false);
      setTodoRoutineModalOpen(true);
      return;
    }
    setTodoEditCategoryInput(catInput);
    setTodoEditDraft({
      ...item,
      ...(occ?.ymd ? { due_kind: 'day' as const, due_date: occ.ymd } : {}),
    });
    setTodoEditModalOpen(true);
  }, [selectedTodoItemId, todoCategories, todoItems]);

  const timetableKeyboardDisabled =
    todoAddModalOpen ||
    todoEditModalOpen ||
    todoRoutineModalOpen ||
    yearCalendarModalOpen ||
    lessonRecordModalOpen ||
    behaviorNoteModalOpen ||
    weeklyNoteUnlockOpen;

  const renderTimetableDayTodos = useCallback(
    (dateIso: string) => (
      <DashboardWeeklyTodoItems
        dateIso={dateIso}
        todos={weeklyTodosByDateIso[dateIso] ?? []}
        onAddTodo={(iso) => void openTodoAddModal(iso)}
        onOpenTodo={(item) => openTodoEditModal(item.id)}
        addPosition="start"
      />
    ),
    [openTodoAddModal, openTodoEditModal, weeklyTodosByDateIso]
  );

  const timetableEmbeddedOptions = useMemo<WeeklyLearningGuideEmbeddedOptions>(
    () => ({
      todoMode: timetableTodoMode,
      onTodoModeChange: setTimetableTodoMode,
      onViewedWeekChange: setTimetableMondayIso,
      renderDayTodos: renderTimetableDayTodos,
      keyboardDisabled: timetableKeyboardDisabled,
      onNavStateChange: setTimetableNavState,
      navControlRef: timetableNavControlRef,
      academicYear: lessonAcademicYear,
      initialTermNumber: lessonActiveTermNumber,
      linkedCalendarDays: timetableLinkedCalendarDays,
      lessonBundle,
    }),
    [
      lessonAcademicYear,
      lessonBundle,
      lessonActiveTermNumber,
      renderTimetableDayTodos,
      timetableKeyboardDisabled,
      timetableLinkedCalendarDays,
      timetableTodoMode,
    ]
  );

  const submitTodoEdit = useCallback(async () => {
    if (!workspaceId || !todoEditDraft) return;
    const original = todoItems.find((it) => it.id === todoEditDraft.id);
    if (!original) return;
    setTodoEditSaving(true);
    try {
      let updatedItem: TodoNoteItemDraft = {
        ...original,
        category_id: todoEditDraft.category_id ?? null,
        due_kind: todoEditDraft.due_kind ?? 'day',
        due_date: todoEditDraft.due_date ?? original.due_date,
        title: todoEditDraft.title ?? '',
        body: todoEditDraft.body ?? '',
        misc: todoEditDraft.misc ?? '',
        completed: !!todoEditDraft.completed,
      };
      let updatedItems = todoItems.map((it) => (it.id === original.id ? updatedItem : it));
      const oldKey = todoItemBucketKey(original);
      const newKey = todoItemBucketKey(updatedItem);
      if (oldKey !== newKey) {
        const maxOrder = updatedItems
          .filter((it) => it.id !== original.id && todoItemBucketKey(it) === newKey)
          .reduce((m, it) => Math.max(m, it.bucket_order), -1);
        updatedItem = { ...updatedItem, bucket_order: maxOrder + 1 };
        updatedItems = updatedItems.map((it) => (it.id === original.id ? updatedItem : it));
      }
      await persistDashboardTodos({ items: updatedItems });
      closeTodoEditModal();
    } catch {
      // ignore
    } finally {
      setTodoEditSaving(false);
    }
  }, [
    workspaceId,
    todoEditDraft,
    todoItems,
    persistDashboardTodos,
    closeTodoEditModal,
  ]);

  const submitTodoDelete = useCallback(async () => {
    if (!workspaceId || !todoEditDraft) return;
    const parentId = parseRoutineOccurrenceId(todoEditDraft.id)?.parentId ?? todoEditDraft.id;
    if (!todoItems.some((it) => it.id === parentId)) return;
    setTodoEditSaving(true);
    try {
      const updatedItems = todoItems.filter((it) => it.id !== parentId);
      await persistDashboardTodos({ items: updatedItems, deletedItemIds: [parentId] });
      closeTodoEditModal();
    } catch {
      // ignore
    } finally {
      setTodoEditSaving(false);
    }
  }, [workspaceId, todoEditDraft, todoItems, persistDashboardTodos, closeTodoEditModal]);

  const submitTodoRoutineDelete = useCallback(async () => {
    if (!workspaceId || !todoRoutineDraft?.id) return;
    const parentId = todoRoutineDraft.id;
    if (!todoItems.some((it) => it.id === parentId)) return;
    setTodoRoutineSaving(true);
    try {
      const updatedItems = todoItems.filter((it) => it.id !== parentId);
      await persistDashboardTodos({ items: updatedItems, deletedItemIds: [parentId] });
      closeTodoRoutineModal();
    } catch {
      // ignore
    } finally {
      setTodoRoutineSaving(false);
    }
  }, [workspaceId, todoRoutineDraft, todoItems, persistDashboardTodos, closeTodoRoutineModal]);

  const submitTodoRoutineEdit = useCallback(async (payload: { draft: TodoNoteItemDraft; routine: TodoRoutineConfig }) => {
    if (!workspaceId || !payload.draft.id) return;
    const original = todoItems.find((it) => it.id === payload.draft.id);
    if (!original) return;
    setTodoRoutineSaving(true);
    try {
      const prevLogs = original.routine?.dayLogs ?? {};
      let updatedItem: TodoNoteItemDraft = {
        ...original,
        title: payload.draft.title,
        body: payload.draft.body,
        misc: payload.draft.misc,
        category_id: payload.draft.category_id ?? original.category_id,
        is_routine: true,
        completed: false,
        routine: {
          ...payload.routine,
          dayLogs: normalizeRoutineDayLogs(prevLogs),
        },
      };
      if (todoRoutineOccurrenceYmd && todoRoutineCompleteTouched) {
        updatedItem = patchRoutineDayLog(updatedItem, todoRoutineOccurrenceYmd, {
          completedOverride: payload.draft.completed,
        });
      }
      const updatedItems = todoItems.map((it) => (it.id === original.id ? updatedItem : it));
      await persistDashboardTodos({ items: updatedItems });
      closeTodoRoutineModal();
    } catch {
      // ignore
    } finally {
      setTodoRoutineSaving(false);
    }
  }, [
    workspaceId,
    todoItems,
    persistDashboardTodos,
    closeTodoRoutineModal,
    todoRoutineOccurrenceYmd,
    todoRoutineCompleteTouched,
  ]);

  const submitTodoAdd = useCallback(async () => {
    if (!workspaceId || !todoAddDraft) return;
    const { data: userData } = await supabase.auth.getUser();
    const uid = userData.user?.id;
    if (!uid) return;
    setTodoAddSaving(true);
    try {
      const bucketKey = todoItemBucketKey({
        due_kind: todoAddDraft.due_kind ?? 'day',
        due_date: todoAddDraft.due_date ?? toISODate(new Date()),
        completed: !!todoAddDraft.completed,
      } as TodoNoteItemDraft);
      const maxOrder = todoItems
        .filter((it) => todoItemBucketKey(it) === bucketKey)
        .reduce((m, it) => Math.max(m, it.bucket_order), -1);
      const newItem: TodoNoteItemDraft = {
        id: crypto.randomUUID(),
        user_id: uid,
        workspace_id: workspaceId,
        category_id: todoAddDraft.category_id ?? null,
        due_kind: todoAddDraft.due_kind ?? 'day',
        due_date: todoAddDraft.due_date ?? toISODate(new Date()),
        title: todoAddDraft.title ?? '',
        body: todoAddDraft.body ?? '',
        misc: todoAddDraft.misc ?? '',
        completed: !!todoAddDraft.completed,
        bucket_order: maxOrder + 1,
      };
      const updatedItems = [...todoItems, newItem];
      await persistDashboardTodos({ items: updatedItems });
      closeTodoAddModal();
    } catch {
      // ignore
    } finally {
      setTodoAddSaving(false);
    }
  }, [workspaceId, todoAddDraft, todoItems, persistDashboardTodos, closeTodoAddModal]);

  /** 멤버 그룹 데이터 로드 */
  const loadMemberGradeLayout = useCallback(async () => {
    if (!workspaceId || !currentGradeId) return;
    try {
      const [layoutsMap, members] = await Promise.all([
        getClassMemberGradeLayoutsMap(workspaceId, [currentGradeId]),
        getClassMembers(workspaceId, currentGradeId),
      ]);
      setMemberGradeLayout(layoutsMap[currentGradeId] ?? { groups: [], membersByGroupId: {} });
      setClassMembers(members);
    } catch {
      // ignore
    }
  }, [workspaceId, currentGradeId]);

  useEffect(() => {
    void loadMemberGradeLayout();
  }, [loadMemberGradeLayout]);

  const behaviorSlot = daySlots[0];
  const behaviorNotesQuery = useAnnualBehaviorNotesQuery({
    workspaceId,
    academicYear: behaviorSlot?.academicYear ?? lessonAcademicYear ?? 0,
    termNumber: (behaviorSlot?.termNumber ?? lessonActiveTermNumber ?? 1) as 1 | 2,
    gradeId: currentGradeId,
    enabled:
      !!workspaceId &&
      !!currentGradeId &&
      (!!behaviorSlot || (lessonViewMode === 'weekly' && !!lessonAcademicYear)),
  });
  const behaviorNotes = behaviorNotesQuery.data ?? [];

  /** 행동특성: dateIso + slotIndex */
  const behaviorNoteByDateSlotKey = useMemo(() => {
    const map = new Map<string, AnnualBehaviorNote>();
    for (const note of behaviorNotes) {
      map.set(weeklyBehaviorRecordKey(note.lesson_date, note.day_row_order), note);
    }
    return map;
  }, [behaviorNotes]);

  const weeklyBehaviorRecordKeySet = useMemo(
    () => new Set(behaviorNoteByDateSlotKey.keys()),
    [behaviorNoteByDateSlotKey]
  );

  /** 행동특성 natural key로 조회 (당일 데일리용) */
  const behaviorNoteBySlotIndex = useMemo(() => {
    const map = new Map<number, AnnualBehaviorNote>();
    const slot0 = daySlots[0];
    if (!slot0) return map;
    for (const note of behaviorNotes) {
      if (note.lesson_date === slot0.dateIso) {
        map.set(note.day_row_order, note);
      }
    }
    return map;
  }, [behaviorNotes, daySlots]);

  /** 선택된 수업 슬롯 */
  const selectedLessonSlot = useMemo(() => {
    if (pinnedLessonSlot) return pinnedLessonSlot;
    if (selectedLessonSlotIndex == null) return null;
    return daySlots.find((s) => s.slotIndex === selectedLessonSlotIndex) ?? null;
  }, [pinnedLessonSlot, selectedLessonSlotIndex, daySlots]);

  /** 슬롯 선택 시 인덱스 유효성 체크 */
  useEffect(() => {
    if (pinnedLessonSlot) return;
    if (selectedLessonSlotIndex == null) return;
    const found = daySlots.find((s) => s.slotIndex === selectedLessonSlotIndex);
    if (!found) setSelectedLessonSlotIndex(null);
  }, [selectedLessonSlotIndex, daySlots, pinnedLessonSlot]);

  useEffect(() => {
    if (selectedLessonSlotIndex == null && !pinnedLessonSlot) return;
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (t.closest('.cp-dashboard-slot--selectable')) return;
      if (t.closest('.cp-dashboard-lesson-record-btn')) return;
      if (t.closest('.cp-dash-weekly-lesson-menu')) return;
      if (t.closest('[role="dialog"]')) return;
      if (t.closest('.cp-modal-backdrop')) return;
      setSelectedLessonSlotIndex(null);
      setPinnedLessonSlot(null);
      setDailyLessonMenu(null);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [pinnedLessonSlot, selectedLessonSlotIndex]);

  useEffect(() => {
    if (lessonViewMode !== 'weekly') setPinnedLessonSlot(null);
  }, [lessonViewMode]);

  useEffect(() => {
    setDailyLessonMenu(null);
  }, [viewDate, lessonViewMode]);

  const toggleDailyLessonMenu = useCallback((slot: DashboardAnnualDaySlot, anchorEl: HTMLElement) => {
    const key = weeklyBehaviorRecordKey(slot.dateIso, slot.slotIndex);
    setDailyLessonMenu((prev) => {
      if (prev && weeklyBehaviorRecordKey(prev.slot.dateIso, prev.slot.slotIndex) === key) {
        return null;
      }
      return { slot, rect: anchorEl.getBoundingClientRect() };
    });
  }, []);

  /** 수업기록 모달: 현재 멤버 목록 */
  const lessonRecordMembers = useMemo(
    () => membersForRecordGroup(memberGradeLayout, lessonRecordMemberGroupId, classMembers),
    [memberGradeLayout, lessonRecordMemberGroupId, classMembers]
  );

  /** 행동특성 모달: 현재 멤버 목록 */
  const dashboardBehaviorAiRef = useRef<AiRecordFeedbackPanelHandle>(null);
  const [dashboardBehaviorAiDirty, setDashboardBehaviorAiDirty] = useState(false);
  const dashboardLessonRecordDirty = useDirtySinceOpen(
    lessonRecordModalOpen && selectedLessonSlot ? selectedLessonSlot.annualCellKey : null,
    JSON.stringify([
      lessonRecordTab,
      lessonRecordTab === 'lesson' ? lessonRecordNote : lessonRecordNoteEval,
      lessonRecordMemberGroupId,
    ])
  );
  const dashboardBehaviorDirty =
    useDirtySinceOpen(
      behaviorNoteModalOpen && selectedLessonSlot ? selectedLessonSlot.annualCellKey : null,
      JSON.stringify([behaviorNote, behaviorNoteMemberGroupId])
    ) || dashboardBehaviorAiDirty;
  const behaviorNoteMembers = useMemo(
    () => membersForRecordGroup(memberGradeLayout, behaviorNoteMemberGroupId, classMembers),
    [memberGradeLayout, behaviorNoteMemberGroupId, classMembers]
  );

  /** 수업기록: 태그 확정 시 그룹 자동 선택 */
  const finalizeLessonHashtagForMemberGroup = useCallback(
    (tagBody: string) => {
      const hits = matchMembersByClassRecordTagKeyword(memberGradeLayout, tagBody);
      if (hits.length === 0) return;
      if (hits.length === 1) {
        setLessonRecordMemberGroupId(hits[0].groupId);
        return;
      }
      setLessonTagGroupPick({ keyword: tagBody, candidates: hits });
    },
    [memberGradeLayout]
  );

  /** 행동특성: 태그 확정 시 그룹 자동 선택 */
  const finalizeBehaviorHashtagForMemberGroup = useCallback(
    (tagBody: string) => {
      const hits = matchMembersByClassRecordTagKeyword(memberGradeLayout, tagBody);
      if (hits.length === 0) return;
      if (hits.length === 1) {
        setBehaviorNoteMemberGroupId(hits[0].groupId);
        return;
      }
      setBehaviorTagGroupPick({ keyword: tagBody, candidates: hits });
    },
    [memberGradeLayout]
  );

  /** 수업기록 context line 생성 */
  const lessonRecordContextLine = useMemo(() => {
    if (!selectedLessonSlot) return '';
    const slot = selectedLessonSlot;
    const dateIso = slot.dateIso;
    const [, m, d] = dateIso.split('-');
    const dParsed = new Date(`${dateIso}T00:00:00`);
    const dow = (['일', '월', '화', '수', '목', '금', '토'] as const)[dParsed.getDay()];
    return `${Number(m)}월${Number(d)}일 (${dow}) ${slot.periodLabel} ${slot.timetableText || slot.templateLabel}`;
  }, [selectedLessonSlot]);

  /** 수업기록 단원 라인 */
  const lessonRecordUnitLine = useMemo(() => {
    if (!selectedLessonSlot) return '';
    return selectedLessonSlot.display.progressUnit ?? '';
  }, [selectedLessonSlot]);

  /** 수업기록 모달 열기 */
  const openLessonRecordModal = useCallback((slotArg?: DashboardAnnualDaySlot) => {
    const target = slotArg ?? selectedLessonSlot;
    if (!target || isDashboardSlotNoLesson(target)) return;
    if (slotArg) {
      setPinnedLessonSlot(slotArg);
      setSelectedLessonSlotIndex(null);
    }
    const d = target.display;
    const tabKind = d.progressClassRecordKind === 'eval_log' ? 'eval_log' : 'lesson';
    const storedNote = d.progressClassRecordNote ?? '';
    setLessonRecordTab(tabKind);
    setLessonRecordNote(tabKind === 'lesson' ? storedNote : '');
    setLessonRecordNoteEval(tabKind === 'eval_log' ? storedNote : '');
    const savedGroupId = d.progressRecordMemberGroupId ?? null;
    const defaultGroupId =
      savedGroupId && memberGradeLayout.groups.some((g) => g.id === savedGroupId)
        ? savedGroupId
        : resolveDefaultRecordGroupId(
            memberGradeLayout,
            loadSelectedClassGroupId(workspaceId, currentGradeId)
          );
    setLessonRecordMemberGroupId(defaultGroupId);
    setLessonRecordEvalMemoMemberGroupId(
      tabKind === 'eval_log' && storedNote.trim() ? defaultGroupId : null
    );
    setLessonEvalTagGenConfirm(null);
    setLessonRecordModalExpanded(false);
    setLessonRecordError(null);
    setLessonRecordModalOpen(true);
  }, [selectedLessonSlot, memberGradeLayout, workspaceId, currentGradeId]);

  /** 수업기록: DOM에서 note 읽기 */
  const readLessonRecordNote = useCallback(() => {
    if (lessonRecordTab === 'eval_log') {
      const domNote = lessonRecordEvalEditorRef.current?.readSerializedNoteFromDom?.();
      return domNote !== undefined ? domNote : lessonRecordNoteEval;
    }
    const domNote = lessonRecordMemoEditorRef.current?.readSerializedNoteFromDom?.();
    return domNote !== undefined ? domNote : lessonRecordNote;
  }, [lessonRecordTab, lessonRecordNote, lessonRecordNoteEval]);

  const applyLessonMemberTags = useCallback((nextNote: string, sourceGroupId: string) => {
    if (lessonRecordTab === 'eval_log') {
      setLessonRecordNoteEval(nextNote);
      setLessonRecordEvalMemoMemberGroupId(sourceGroupId);
    } else {
      setLessonRecordNote(nextNote);
    }
    setLessonRecordMemberGroupId(sourceGroupId);
    setLessonRecordError(null);
  }, [lessonRecordTab]);

  const handleGenerateLessonMemberTags = useCallback(() => {
    const gid = (lessonRecordMemberGroupId ?? '').trim();
    if (!gid) {
      setLessonRecordError('멤버 그룹을 선택한 뒤 멤버 태그 생성을 눌러 주세요.');
      return;
    }
    const members = membersForRecordGroup(memberGradeLayout, gid, classMembers);
    if (members.length === 0) return;
    const nextNote = buildDefaultEvalMemberTagsNote(members);
    const current =
      lessonRecordTab === 'eval_log'
        ? (lessonRecordEvalEditorRef.current?.readSerializedNoteFromDom?.() ?? lessonRecordNoteEval)
        : (lessonRecordMemoEditorRef.current?.readSerializedNoteFromDom?.() ?? lessonRecordNote);
    if (evalSerializedNoteHasNonTagText(current)) {
      setLessonEvalTagGenConfirm({ nextNote, sourceGroupId: gid });
      return;
    }
    applyLessonMemberTags(nextNote, gid);
  }, [
    applyLessonMemberTags,
    classMembers,
    lessonRecordMemberGroupId,
    lessonRecordNote,
    lessonRecordNoteEval,
    lessonRecordTab,
    memberGradeLayout,
  ]);

  const lessonRecordCanDelete = useMemo(() => {
    if (!selectedLessonSlot) return false;
    const stored = parseMemoFieldStorageDeep(selectedLessonSlot.recordClassTextRaw ?? '');
    const storedText = stored.text.trim();
    if (stored.note.trim()) return true;
    if (storedText === 'lesson' || storedText === 'eval_log') return true;
    if (lessonRecordNote.trim() || lessonRecordNoteEval.trim()) return true;
    return lessonRecordTab === 'lesson' || lessonRecordTab === 'eval_log';
  }, [lessonRecordNote, lessonRecordNoteEval, lessonRecordTab, selectedLessonSlot]);

  /** 수업기록 저장 */
  const saveLessonRecord = useCallback(async (opts?: { delete?: boolean }) => {
    if (!workspaceId || !currentGradeId || !selectedLessonSlot) return;
    setLessonRecordSaving(true);
    setLessonRecordError(null);
    selfSavingRef.current = true;
    try {
      const preserved = parseMemoFieldStorageDeep(selectedLessonSlot.recordClassTextRaw ?? '');
      const preservedBehavior = preserved.behaviorNote;
      const recordClassText = opts?.delete
        ? serializeClassRecordCellStorage('', '', preservedBehavior)
        : serializeClassRecordCellStorage(
            lessonRecordTab,
            readLessonRecordNote().trim(),
            preservedBehavior,
            preserved.assignmentMemoItemId,
            preserved.assignmentMemoItemTitle,
            preserved.assignmentMemoGroupId,
            preserved.assignmentMemoGroupTitle,
            preserved.aiFeedback
          );
      const recordMemberGroupId = opts?.delete ? null : lessonRecordMemberGroupId;
      let savedScopedScope: { scopeType: ProgramScopeType; scopeKey: string } | null = null;

      if (selectedLessonSlot.fixedContentRowId) {
        const fixedCache = readProgramFixedRowsCacheOrQuery(
          workspaceId,
          selectedLessonSlot.academicYear,
          selectedLessonSlot.termNumber,
          currentGradeId
        );
        const existingRow = fixedCache.found
          ? fixedCache.rows.find((r) => r.id === selectedLessonSlot.fixedContentRowId)
          : null;
        if (existingRow) {
          await upsertProgramFixedContentRows([
            {
              id: existingRow.id,
              workspace_id: workspaceId,
              grade_id: currentGradeId,
              academic_year: selectedLessonSlot.academicYear,
              term_number: selectedLessonSlot.termNumber,
              event_name: existingRow.event_name,
              order_index: existingRow.order_index,
              source_annual_cell_key: existingRow.source_annual_cell_key,
              timetable_text: existingRow.timetable_text,
              unit_text: existingRow.unit_text,
              learning_text: existingRow.learning_text,
              page_text: existingRow.page_text,
              assistant_page_text: existingRow.assistant_page_text,
              current_period_text: existingRow.current_period_text,
              total_period_text: existingRow.total_period_text,
              material_text: existingRow.material_text,
              record_cross_curricular_text: existingRow.record_cross_curricular_text,
              record_memo_text: existingRow.record_memo_text,
              record_evaluation_text: existingRow.record_evaluation_text,
              record_class_text: recordClassText,
              record_member_group_id: recordMemberGroupId,
              bg_color_by_field: existingRow.bg_color_by_field ?? {},
            },
          ]);
        }
      } else if (selectedLessonSlot.scopedContentScopeKey && selectedLessonSlot.scopedContentScopeType) {
        const targetScopeKey = selectedLessonSlot.scopedContentScopeKey;
        const targetScopeType = selectedLessonSlot.scopedContentScopeType as ProgramScopeType;
        /** 행 id로 기록 칸만 고친다. 행이 없으면 스코프 안 위치에 (중복 없이) 만든 뒤 고친다 */
        await saveScopedRecordFieldsAtPosition(
          {
            workspaceId,
            academicYear: selectedLessonSlot.academicYear,
            termNumber: selectedLessonSlot.termNumber,
            gradeId: currentGradeId,
            scopeType: targetScopeType,
            scopeKey: targetScopeKey,
          },
          { rowId: selectedLessonSlot.scopedContentRowId, position: selectedLessonSlot.scopedContentOrderIndex },
          { record_class_text: recordClassText, record_member_group_id: recordMemberGroupId }
        );
        savedScopedScope = { scopeType: targetScopeType, scopeKey: targetScopeKey };
      } else {
        throw new Error('해당 수업의 프로그램 데이터가 없습니다. 프로그램 페이지에서 먼저 데이터를 생성해주세요.');
      }

      if (selectedLessonSlot.fixedContentRowId) {
        clearProgramLessonRecordCaches(
          workspaceId,
          selectedLessonSlot.academicYear,
          selectedLessonSlot.termNumber,
          currentGradeId,
          { type: 'fixed' }
        );
      } else if (savedScopedScope) {
        clearProgramLessonRecordCaches(
          workspaceId,
          selectedLessonSlot.academicYear,
          selectedLessonSlot.termNumber,
          currentGradeId,
          {
            type: 'scoped',
            scopeType: savedScopedScope.scopeType,
            scopeKey: savedScopedScope.scopeKey,
          }
        );
      }

      bumpLessonRefresh();
      setLessonRecordModalOpen(false);
      setTimeout(() => {
        selfSavingRef.current = false;
        emitProgramProgressSaved({
          workspaceId,
          academicYear: selectedLessonSlot.academicYear,
          termNumber: selectedLessonSlot.termNumber,
          gradeId: currentGradeId,
        });
      }, 100);
    } catch (e) {
      setLessonRecordError(e instanceof Error ? e.message : opts?.delete ? '삭제에 실패했습니다.' : '저장에 실패했습니다.');
      selfSavingRef.current = false;
    } finally {
      setLessonRecordSaving(false);
    }
  }, [bumpLessonRefresh, workspaceId, currentGradeId, selectedLessonSlot, lessonRecordTab, lessonRecordMemberGroupId, readLessonRecordNote]);

  /** 행동특성 context line 생성 */
  const behaviorNoteContextLine = useMemo(() => {
    if (!selectedLessonSlot) return '';
    const slot = selectedLessonSlot;
    const dateIso = slot.dateIso;
    const [, m, d] = dateIso.split('-');
    const dParsed = new Date(`${dateIso}T00:00:00`);
    const dow = (['일', '월', '화', '수', '목', '금', '토'] as const)[dParsed.getDay()];
    return `${Number(m)}월${Number(d)}일 (${dow}) ${slot.periodLabel}`;
  }, [selectedLessonSlot]);

  /** 연간 달력에서 날짜 선택 → 데일리(해당일) / 위클리(해당 주) */
  const selectDateFromYearCalendar = useCallback(
    (iso: string) => {
      const picked = parseISODate(iso);
      if (Number.isNaN(picked.getTime())) return;
      if (lessonViewMode === 'weekly') {
        setViewDate(clampDateToPlannerWeeks(picked, plannerAcademicYear));
      } else if (lessonViewMode === 'calendar') {
        setViewDate(clampDateToPlannerWeeks(picked, plannerAcademicYear));
        setCalendarMonth(clampMonthToPlanner(picked, plannerAcademicYear));
      } else {
        setViewDate(picked);
      }
      setYearCalendarModalOpen(false);
    },
    [lessonViewMode, plannerAcademicYear]
  );

  /** 행동특성 모달 열기 */
  const openBehaviorNoteModal = useCallback((slotArg?: DashboardAnnualDaySlot) => {
    const target = slotArg ?? selectedLessonSlot;
    if (!target) return;
    if (slotArg) {
      setPinnedLessonSlot(slotArg);
      setSelectedLessonSlotIndex(null);
    }
    const key = weeklyBehaviorRecordKey(target.dateIso, target.slotIndex);
    const existing = behaviorNoteByDateSlotKey.get(key);
    setBehaviorNote(existing?.behavior_note ?? '');
    const savedGroupId = existing?.record_member_group_id ?? null;
    setBehaviorNoteMemberGroupId(
      savedGroupId && memberGradeLayout.groups.some((g) => g.id === savedGroupId)
        ? savedGroupId
        : resolveDefaultRecordGroupId(
            memberGradeLayout,
            loadSelectedClassGroupId(workspaceId, currentGradeId)
          )
    );
    setBehaviorNoteModalExpanded(false);
    setBehaviorNoteError(null);
    setBehaviorNoteModalOpen(true);
  }, [selectedLessonSlot, behaviorNoteByDateSlotKey, memberGradeLayout, workspaceId, currentGradeId]);

  const openWeeklySlotPresentation = useCallback(
    (slot: DashboardAnnualDaySlot) => {
      if (!workspaceId?.trim() || !currentGradeId?.trim()) return;
      if (isDashboardSlotNoLesson(slot)) return;
      openDashboardAnnualSlotPresentation({
        slot,
        workspaceId,
        gradeId: currentGradeId,
        academicYear: slot.academicYear || lessonAcademicYear || new Date().getFullYear(),
        termNumber: (slot.termNumber ?? lessonActiveTermNumber ?? 1) as 1 | 2,
      });
    },
    [workspaceId, currentGradeId, lessonAcademicYear, lessonActiveTermNumber]
  );

  const openDashboardSlotProgram = useCallback(
    (slot: DashboardAnnualDaySlot) => {
      if (!currentGradeId?.trim()) return;
      const path = buildDashboardSlotProgramPath(slot, {
        gradeId: currentGradeId,
        academicYear: lessonAcademicYear || new Date().getFullYear(),
        fallbackTermNumber: (lessonActiveTermNumber ?? null) as 1 | 2 | null,
        annualProjectPrograms: lessonBundle?.annualProjectPrograms ?? [],
      });
      if (path) navigate(path);
    },
    [currentGradeId, lessonAcademicYear, lessonActiveTermNumber, lessonBundle?.annualProjectPrograms, navigate]
  );

  /** 행동특성: DOM에서 note 읽기 */
  const readBehaviorNote = useCallback(() => {
    const domNote = behaviorMemoEditorRef.current?.readSerializedNoteFromDom?.();
    return domNote !== undefined ? domNote : behaviorNote;
  }, [behaviorNote]);

  /** 행동특성 저장 */
  const saveBehaviorNote = useCallback(async (aiFeedback?: string): Promise<boolean> => {
    if (!workspaceId || !currentGradeId || !selectedLessonSlot) return false;
    setBehaviorNoteSaving(true);
    setBehaviorNoteError(null);
    try {
      await upsertAnnualBehaviorNote({
        workspace_id: workspaceId,
        grade_id: currentGradeId,
        academic_year: selectedLessonSlot.academicYear,
        term_number: selectedLessonSlot.termNumber,
        lesson_date: selectedLessonSlot.dateIso,
        day_row_order: selectedLessonSlot.slotIndex,
        behavior_note: readBehaviorNote().trim(),
        record_member_group_id: behaviorNoteMemberGroupId,
        source_annual_cell_key_snapshot: selectedLessonSlot.annualCellKey,
        period_label_snapshot: selectedLessonSlot.periodLabel,
        timetable_text_snapshot: selectedLessonSlot.timetableText,
        ...(aiFeedback !== undefined ? { ai_feedback: aiFeedback } : {}),
      });
      emitAnnualBehaviorNotesSaved({
        workspaceId,
        academicYear: selectedLessonSlot.academicYear,
        termNumber: selectedLessonSlot.termNumber,
        gradeId: currentGradeId,
      });
      setBehaviorNoteModalOpen(false);
      return true;
    } catch (e) {
      setBehaviorNoteError(e instanceof Error ? e.message : '저장에 실패했습니다.');
      return false;
    } finally {
      setBehaviorNoteSaving(false);
    }
  }, [workspaceId, currentGradeId, selectedLessonSlot, behaviorNoteMemberGroupId, readBehaviorNote]);

  const behaviorNoteCanDelete = useMemo(() => {
    if (!selectedLessonSlot) return false;
    const key = weeklyBehaviorRecordKey(selectedLessonSlot.dateIso, selectedLessonSlot.slotIndex);
    const stored = (behaviorNoteByDateSlotKey.get(key)?.behavior_note ?? '').trim();
    if (stored) return true;
    return !!behaviorNote.trim();
  }, [behaviorNote, behaviorNoteByDateSlotKey, selectedLessonSlot]);

  const deleteBehaviorNote = useCallback(async () => {
    if (!workspaceId || !currentGradeId || !selectedLessonSlot) return;
    setBehaviorNoteSaving(true);
    setBehaviorNoteError(null);
    try {
      await deleteAnnualBehaviorNoteByNaturalKey({
        workspaceId,
        gradeId: currentGradeId,
        academicYear: selectedLessonSlot.academicYear,
        termNumber: selectedLessonSlot.termNumber,
        lessonDate: selectedLessonSlot.dateIso,
        dayRowOrder: selectedLessonSlot.slotIndex,
      });
      setBehaviorNoteModalOpen(false);
    } catch (e) {
      setBehaviorNoteError(e instanceof Error ? e.message : '삭제에 실패했습니다.');
    } finally {
      setBehaviorNoteSaving(false);
    }
  }, [workspaceId, currentGradeId, selectedLessonSlot]);

  useEffect(() => {
    return () => {
      if (holdDelayTimerRef.current != null) {
        window.clearTimeout(holdDelayTimerRef.current);
      }
      if (holdRafRef.current != null) {
        cancelAnimationFrame(holdRafRef.current);
      }
    };
  }, []);

  const plannerTodoBoardPicker = (
    <div className="cp-dash-planner-section-head-picker">
      <TodoNoteBoardPicker
        boards={todoBoards}
        selectedBoardId={todoSelectedBoardId}
        open={todoBoardPickerOpen}
        onOpenChange={setTodoBoardPickerOpen}
        onSelect={(id) => selectDashboardTodoBoard(id)}
        portalMenu
      />
    </div>
  );

  return (
    <section className="cp-dashboard-page" aria-labelledby="cp-dashboard-page-title">
      <div
        className={
          'cp-dashboard-page__view-bar' + (isPlannerLessonView ? ' is-weekly' : '') + (isCalendarLessonView ? ' is-calendar' : '')
        }
      >
        <div className="cp-dashboard-page__view-bar-start">
          <div className="cp-dashboard-page__view-bar-left">
            <h2 id="cp-dashboard-page-title" className="cp-page-title cp-dashboard-page__title">
              대시보드
            </h2>
            <div
              className="cp-todo-note-view-toggle"
              role="tablist"
              aria-label="대시보드 보기"
              data-tutorial-dash="view-toggle"
            >
              <button
                type="button"
                role="tab"
                aria-selected={lessonViewMode === 'daily'}
                className={`cp-todo-note-view-btn${lessonViewMode === 'daily' ? ' is-active' : ''}`}
                data-tutorial-dash="view-daily"
                onClick={() => {
                  setYearCalendarModalOpen(false);
                  setLessonViewMode('daily');
                }}
              >
                데일리
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={isWeeklyLessonView}
                className={`cp-todo-note-view-btn${isWeeklyLessonView ? ' is-active' : ''}`}
                data-tutorial-dash="view-weekly"
                onClick={() => setLessonViewMode('weekly')}
              >
                위클리
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={isTimetableLessonView}
                className={`cp-todo-note-view-btn${isTimetableLessonView ? ' is-active' : ''}`}
                data-tutorial-dash="view-timetable"
                onClick={() => setLessonViewMode('timetable')}
              >
                시간표
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={isCalendarLessonView}
                className={`cp-todo-note-view-btn${isCalendarLessonView ? ' is-active' : ''}`}
                data-tutorial-dash="view-calendar"
                onClick={() => setLessonViewMode('calendar')}
              >
                캘린더
              </button>
            </div>
          </div>
          <div
            className={
              'cp-dashboard-page__view-bar-options' +
              (isCalendarLessonView || isWeeklyLessonView ? '' : ' is-spacer')
            }
            aria-hidden={isCalendarLessonView || isWeeklyLessonView ? undefined : true}
          >
              {isCalendarLessonView ? (
                <div
                  className="cp-annual-top-saturday-toggle cp-dash-cal-fixed-only-toggle"
                  aria-label="고정(행사) 프로그램만 표시"
                >
                  <span className="cp-weekly-schedule-saturday-toggle-label">
                    <span className="cp-todo-note-toggle-label-full">고정(행사)만 표시</span>
                    <span className="cp-todo-note-toggle-label-short">행사만</span>
                  </span>
                  <button
                    type="button"
                    className={'team-auto-approve-toggle' + (calendarFixedEventsOnly ? ' on' : '')}
                    aria-pressed={calendarFixedEventsOnly}
                    aria-label="고정(행사) 프로그램만 표시"
                    title="켜면 고정(행사) 프로그램만 보이고 나머지 시간표는 숨깁니다"
                    onClick={() => setCalendarFixedEventsOnly((v) => !v)}
                  >
                    <span className="team-auto-approve-toggle-slider" data-on={!calendarFixedEventsOnly}>
                      OFF
                    </span>
                    <span className="team-auto-approve-toggle-slider" data-on={calendarFixedEventsOnly}>
                      ON
                    </span>
                  </button>
                </div>
              ) : null}
              {isWeeklyLessonView ? (
                <div
                  className={
                    'cp-annual-top-saturday-toggle cp-dash-weekly-days-layout-toggle' +
                    (weeklyDaysAutoVertical ? ' is-auto-locked' : '')
                  }
                  aria-label="위클리 세로정렬"
                  title={
                    weeklyDaysAutoVertical
                      ? '화면이 좁아 세로정렬이 자동으로 적용됩니다'
                      : '요일 블럭 세로정렬'
                  }
                >
                  <span className="cp-weekly-schedule-saturday-toggle-label">세로정렬</span>
                  <button
                    type="button"
                    className={'team-auto-approve-toggle' + (weeklyDaysVertical ? ' on' : '')}
                    aria-pressed={weeklyDaysVertical}
                    aria-label="위클리 세로정렬"
                    disabled={weeklyDaysAutoVertical}
                    onClick={() => {
                      if (weeklyDaysAutoVertical) return;
                      setWeeklyDaysVerticalPref(!weeklyDaysVerticalPref);
                    }}
                  >
                    <span className="team-auto-approve-toggle-slider" data-on={!weeklyDaysVertical}>
                      OFF
                    </span>
                    <span className="team-auto-approve-toggle-slider" data-on={weeklyDaysVertical}>
                      ON
                    </span>
                  </button>
                </div>
              ) : null}
              {isCalendarLessonView && isMobileCalendarLayout ? (
                <div
                  className="cp-annual-top-saturday-toggle cp-todo-note-calendar-layout-toggle"
                  aria-label="캘린더 맞춤·확장"
                >
                  <button
                    type="button"
                    className={
                      'team-auto-approve-toggle' +
                      (calendarLayoutMode === 'expand' ? ' on' : '')
                    }
                    onClick={() =>
                      setCalendarLayoutMode((m) => (m === 'fit' ? 'expand' : 'fit'))
                    }
                    aria-pressed={calendarLayoutMode === 'fit'}
                    aria-label={
                      calendarLayoutMode === 'fit'
                        ? '맞춤 모드 — 누르면 확장'
                        : '확장 모드 — 누르면 맞춤'
                    }
                    title={
                      calendarLayoutMode === 'fit'
                        ? '맞춤: 가로에 일~토 맞춤 표시'
                        : '확장: 가로·세로 스크롤로 탐색'
                    }
                  >
                    <span
                      className="team-auto-approve-toggle-slider"
                      data-on={calendarLayoutMode === 'fit'}
                    >
                      맞춤
                    </span>
                    <span
                      className="team-auto-approve-toggle-slider"
                      data-on={calendarLayoutMode === 'expand'}
                    >
                      확장
                    </span>
                  </button>
                </div>
              ) : null}
          </div>
        </div>
        <div className="cp-dash-weekly-center-actions">
          <div className="cp-apn-header-icon-btn-wrap cp-dash-weekly-cal-btn-wrap">
            <button
              type="button"
              className="cp-apn-header-icon-btn"
              data-tutorial-dash="open-calendar"
              onClick={() => setYearCalendarModalOpen(true)}
              aria-label="달력"
            >
              <img
                src={`${process.env.PUBLIC_URL || ''}/weektable.svg`}
                alt=""
                width={28}
                height={28}
                decoding="async"
              />
            </button>
            <span className="cp-apn-header-icon-tooltip" role="tooltip">
              달력
            </span>
          </div>
          {isTimetableLessonView ? (
            <div className="cp-apn-header-icon-btn-wrap cp-dash-weekly-mode-btn-wrap">
              <button
                type="button"
                className={
                  'cp-apn-header-icon-btn cp-dash-weekly-mode-btn' +
                  (timetableTodoMode ? ' is-notes' : '')
                }
                aria-pressed={timetableTodoMode}
                aria-label={timetableTodoMode ? '시간표 보기로 돌아가기' : '요일별 TODO로 전환'}
                title={timetableTodoMode ? '시간표 보기' : '요일별 TODO'}
                onClick={() => setTimetableTodoMode((v) => !v)}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d={timetableTodoMode ? 'M6 14.5L12 8.5L18 14.5' : 'M6 9.5L12 15.5L18 9.5'}
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <span className="cp-apn-header-icon-tooltip" role="tooltip">
                {timetableTodoMode ? '시간표 보기' : '요일별 TODO'}
              </span>
            </div>
          ) : null}
          {isWeeklyLessonView ? (
            <div className="cp-apn-header-icon-btn-wrap cp-dash-weekly-mode-btn-wrap">
              <button
                type="button"
                className={
                  'cp-apn-header-icon-btn cp-dash-weekly-mode-btn' +
                  (weeklyNotesMode ? ' is-notes' : '')
                }
                aria-pressed={weeklyNotesMode}
                aria-label={weeklyNotesMode ? '수업 보기로 돌아가기' : '일자별 기록으로 전환'}
                title={weeklyNotesMode ? '수업 보기' : '일자별 기록'}
                onClick={() => setWeeklyNotesMode((v) => !v)}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
                  {weeklyNotesMode ? (
                    <path
                      d="M6 14.5L12 8.5L18 14.5"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  ) : (
                    <path
                      d="M6 9.5L12 15.5L18 9.5"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  )}
                </svg>
              </button>
              <span className="cp-apn-header-icon-tooltip" role="tooltip">
                {weeklyNotesMode ? '수업 보기' : '일자별 기록'}
              </span>
            </div>
          ) : null}
        </div>
      </div>
      <div className="cp-dashboard-page__fill">
        {isWeeklyLessonView ? (
          <div
            className={
              'cp-dash-weekly-wrap' +
              (weeklyDaysVertical ? ' is-days-vertical' : '') +
              (weeklyNotesMode ? ' is-notes-mode' : '')
            }
            data-tutorial-dash="weekly-wrap"
          >
            <DashboardPlannerSectionHead
              academicYear={plannerAcademicYear}
              termNumber={plannerTermNumber}
              tutorialAttr="weekly-toolbar"
              afterLabel={plannerTodoBoardPicker}
              center={
                <>
                  <button
                    type="button"
                    className="cp-dashboard-date-nav"
                    disabled={!canPrevPlannerWeek}
                    onClick={() => goPlannerWeek(-1)}
                    aria-label="이전 주"
                  >
                    {'<'}
                  </button>
                  <button
                    type="button"
                    className="cp-dash-planner-section-head-week-btn"
                    onClick={() => setViewDate(clampDateToPlannerWeeks(new Date(), plannerAcademicYear))}
                    aria-label="오늘이 속한 주로 이동"
                  >
                    <DashboardSemesterWeekDigit
                      weekIndex={semesterWeekIndex}
                      className="cp-dash-planner-section-head-digit"
                    />
                  </button>
                  <button
                    type="button"
                    className="cp-dashboard-date-nav"
                    disabled={!canNextPlannerWeek}
                    onClick={() => goPlannerWeek(1)}
                    aria-label="다음 주"
                  >
                    {'>'}
                  </button>
                </>
              }
            />
            <div
              ref={weekSwipe.viewportRef}
              className={weekSwipe.viewportClassName}
            >
              <div
                ref={weekSwipe.trackRef}
                className={weekSwipe.trackClassName}
                style={weekSwipe.trackStyle}
              >
                <DashboardWeeklyWeekPane
                  mondayIso={prevMondayIso}
                  interactive={false}
                  weeklyNotesMode={weeklyNotesMode}
                  holidayIsoSet={dashboardHolidayIsoSet}
                  holidayNoteByIso={dashboardHolidayNoteByIso}
                  slotsByDateIso={weeklySlotsByDateIso}
                  todosByDateIso={weeklyTodosByDateIso}
                  behaviorRecordKeySet={weeklyBehaviorRecordKeySet}
                  periodTimeByKey={weeklyPeriodTimeByKey}
                  daysVertical={weeklyDaysVertical}
                  weekNotes={adjacentNotesByMonday.prev}
                  savedWeekNotes={adjacentNotesByMonday.prev}
                  dailyNoteCategories={dailyNoteCategories}
                  contentsHidden={dailyNoteViewLocked}
                />
                <DashboardWeeklyWeekPane
                  mondayIso={mondayIso}
                  interactive
                  weeklyNotesMode={weeklyNotesMode}
                  holidayIsoSet={dashboardHolidayIsoSet}
                  holidayNoteByIso={dashboardHolidayNoteByIso}
                  slotsByDateIso={weeklySlotsByDateIso}
                  todosByDateIso={weeklyTodosByDateIso}
                  behaviorRecordKeySet={weeklyBehaviorRecordKeySet}
                  periodTimeByKey={weeklyPeriodTimeByKey}
                  daysVertical={weeklyDaysVertical}
                  weekNotes={dailyWeekNotes}
                  savedWeekNotes={dailyWeekNotesBaseline}
                  dailyNoteCategories={dailyNoteCategories}
                  contentsHidden={dailyNoteViewLocked}
                  onAddTodo={(dateIso) => void openTodoAddModal(dateIso)}
                  onOpenTodo={(item) => openTodoEditModal(item.id)}
                  onOpenPresentation={openWeeklySlotPresentation}
                  onOpenProgram={openDashboardSlotProgram}
                  onOpenLessonRecord={(slot) => openLessonRecordModal(slot)}
                  onOpenBehaviorRecord={(slot) => openBehaviorNoteModal(slot)}
                  onChangeWeekNotes={handleWeekNotesChange}
                  onUnlockRequest={() => {
                    setWeeklyNoteUnlockError('');
                    setWeeklyNoteUnlockOpen(true);
                  }}
                />
                <DashboardWeeklyWeekPane
                  mondayIso={nextMondayIso}
                  interactive={false}
                  weeklyNotesMode={weeklyNotesMode}
                  holidayIsoSet={dashboardHolidayIsoSet}
                  holidayNoteByIso={dashboardHolidayNoteByIso}
                  slotsByDateIso={weeklySlotsByDateIso}
                  todosByDateIso={weeklyTodosByDateIso}
                  behaviorRecordKeySet={weeklyBehaviorRecordKeySet}
                  periodTimeByKey={weeklyPeriodTimeByKey}
                  daysVertical={weeklyDaysVertical}
                  weekNotes={adjacentNotesByMonday.next}
                  savedWeekNotes={adjacentNotesByMonday.next}
                  dailyNoteCategories={dailyNoteCategories}
                  contentsHidden={dailyNoteViewLocked}
                />
              </div>
            </div>
          </div>
        ) : isTimetableLessonView ? (
          <div className="cp-dash-timetable-wrap">
            <DashboardPlannerSectionHead
              academicYear={plannerAcademicYear}
              termNumber={timetableNavState?.termNumber ?? plannerTermNumber}
              tutorialAttr="timetable-toolbar"
              afterLabel={plannerTodoBoardPicker}
              center={
                <>
                  <button
                    type="button"
                    className="cp-dashboard-date-nav"
                    disabled={!timetableNavState?.canPrev}
                    onClick={() => timetableNavControlRef.current?.prev()}
                    aria-label="이전 주"
                  >
                    {'<'}
                  </button>
                  <button
                    type="button"
                    className="cp-dash-planner-section-head-week-btn"
                    onClick={() => timetableNavControlRef.current?.today()}
                    aria-label="오늘이 속한 주로 이동"
                  >
                    <DashboardSemesterWeekDigit
                      weekIndex={timetableNavState?.weekIndex ?? timetableFallbackWeekIndex}
                      className="cp-dash-planner-section-head-digit"
                    />
                  </button>
                  <button
                    type="button"
                    className="cp-dashboard-date-nav"
                    disabled={!timetableNavState?.canNext}
                    onClick={() => timetableNavControlRef.current?.next()}
                    aria-label="다음 주"
                  >
                    {'>'}
                  </button>
                </>
              }
            />
            <WeeklyLearningGuidePage
              workspaceId={workspaceId}
              currentGradeId={currentGradeId}
              grades={grades}
              embedded={timetableEmbeddedOptions}
            />
          </div>
        ) : isCalendarLessonView ? (
          <DashboardWeeklyCalendar
            cells={calendarCells}
            month={calendarMonth}
            holidayIsoSet={dashboardHolidayIsoSet}
            holidayNoteByIso={dashboardHolidayNoteByIso}
            slotsByDateIso={calendarSlotsByDateIso}
            todosByDateIso={calendarTodosByDateIso}
            layoutMode={calendarLayoutMode}
            isMobileLayout={isMobileCalendarLayout}
            fixedEventsOnly={calendarFixedEventsOnly}
            sectionHead={
              <DashboardPlannerSectionHead
                academicYear={plannerAcademicYear}
                termNumber={plannerTermNumber}
                embedded
                tutorialAttr="weekly-calendar-toolbar"
                afterLabel={plannerTodoBoardPicker}
                center={
                  <>
                    <button
                      type="button"
                      className="cp-dashboard-date-nav"
                      disabled={!canPrevCalendarMonth}
                      onClick={() => goCalendarMonth(-1)}
                      aria-label="이전 달"
                    >
                      {'<'}
                    </button>
                    <button
                      type="button"
                      className="cp-dash-planner-section-head-month-btn"
                      onClick={() => goCalendarMonth(0)}
                      aria-label="이번 달로 이동"
                    >
                      {calendarMonth.getFullYear()}년 {calendarMonth.getMonth() + 1}월
                    </button>
                    <button
                      type="button"
                      className="cp-dashboard-date-nav"
                      disabled={!canNextCalendarMonth}
                      onClick={() => goCalendarMonth(1)}
                      aria-label="다음 달"
                    >
                      {'>'}
                    </button>
                  </>
                }
              />
            }
            onAddTodo={(dateIso) => void openTodoAddModal(dateIso)}
            onOpenTodo={(itemId) => openTodoEditModal(itemId)}
            onOpenPresentation={openWeeklySlotPresentation}
            onOpenProgram={openDashboardSlotProgram}
            onOpenLessonRecord={(slot) => openLessonRecordModal(slot)}
            onOpenBehaviorRecord={(slot) => openBehaviorNoteModal(slot)}
          />
        ) : (
        <div className="cp-dashboard-sections cp-dashboard-sections--with-todo" role="presentation">
          <article
            className="cp-dashboard-block cp-dashboard-block--planner-sheet cp-dashboard-block--daily-lessons"
            aria-labelledby="cp-dashboard-today-section-title"
            data-tutorial-dash="daily-lessons"
          >
            <h3 className="cp-dashboard-block__head cp-dashboard-lesson-head" id="cp-dashboard-today-section-title">
              <div
                className={
                  'cp-dashboard-lesson-head-left' +
                  (isDailyWeekend ? ' is-weekend' : '') +
                  (dailyHolidayName ? ' is-holiday' : '')
                }
                data-tutorial-dash="daily-date-nav"
              >
                <span
                  className="cp-dashboard-daily-head-date"
                  aria-current={isViewingToday ? 'date' : undefined}
                >
                  {dailyDateLabel}
                </span>
                <span className="cp-dashboard-daily-head-divider" aria-hidden />
                <span className="cp-dashboard-daily-head-weekday">{dailyWeekdayKo}</span>
                {dailyHolidayName ? (
                  <span className="cp-dash-weekly-day-holiday-name">{dailyHolidayName}</span>
                ) : null}
              </div>
              <div className="cp-dashboard-lesson-head-right">
                <button
                  type="button"
                  className={'cp-dashboard-daily-today-btn' + (isViewingToday ? ' is-today' : '')}
                  onClick={() => setViewDate(new Date())}
                  aria-label="오늘 날짜로 이동"
                >
                  <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden focusable="false">
                    <rect x="2" y="3" width="12" height="11" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M2 6.5h12M5.5 1.5v3M10.5 1.5v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M5 9h1.5M7.25 9h1.5M9.5 9H11M5 11.5h1.5M7.25 11.5h1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                  </svg>
                  오늘
                </button>
                <div className="cp-dashboard-lesson-date-nav-group" role="group" aria-label="날짜 이동">
                  <button
                    type="button"
                    className="cp-dashboard-date-nav cp-dashboard-daily-nav-btn"
                    onClick={() => setViewDate((prev) => addDaysForDashboardNav(prev, -1))}
                    aria-label="이전 날"
                  >
                    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden focusable="false">
                      <path d="M10 3.5 5.5 8l4.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="cp-dashboard-date-nav cp-dashboard-daily-nav-btn"
                    onClick={() => setViewDate((prev) => addDaysForDashboardNav(prev, 1))}
                    aria-label="다음 날"
                  >
                    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden focusable="false">
                      <path d="M6 3.5 10.5 8 6 12.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              </div>
            </h3>
            <div className="cp-dashboard-block__body">
              {!canShowMain ? (
                <p className="cp-dashboard-placeholder">{EMPTY_SECTION_MESSAGE}</p>
              ) : showWeekendHint ? (
                <p className="cp-dashboard-placeholder" role="status">
                  이 날짜는 연간 수업 일정에 포함되지 않을 수 있습니다(예: 일요일).
                </p>
              ) : lessonError && weekdayKey != null && !lessonCachePending ? (
                <p className="cp-dashboard-placeholder" role="alert">
                  대시보드 데이터를 불러오지 못했습니다. {lessonError}
                </p>
              ) : lessonCachePending ? (
                <p className="cp-dashboard-placeholder" role="status" aria-live="polite">
                  수업·진도 데이터를 불러오는 중…
                </p>
              ) : showNoScheduleHint ? (
                <p className="cp-dashboard-placeholder" role="status">
                  이 날짜에 표시할 수업 편제가 없습니다. 클래스 편제·연간시간표·프로그램 진도를 확인해 주세요.
                </p>
              ) : showSlotGrid ? (
                <div className="cp-dashboard-slots">
                  {daySlots.map((slot) => {
                    const d = slot.display;
                    const noLesson = isDashboardSlotNoLesson(slot);
                    const isCalendarHolidayRow = slot.timetableSlotKind === 'holiday';
                    const periodLabel = (slot.periodLabel ?? '').trim();
                    const pyeonje = (slot.templateLabel ?? '').trim();
                    const timeLabel = dailyPeriodTimeBySlotIndex.get(slot.slotIndex) ?? '';
                    const unitPart = formatUnitPart(d);
                    const pagePart = formatPageParen(d);
                    const learn = formatLearning(d);
                    const hex = (slot.timetableColorHex ?? '').trim();
                    const hasTimetableBg = Boolean(hex) && /^#[0-9A-Fa-f]{6}$/.test(hex);
                    const slotTint =
                      isCalendarHolidayRow && noLesson
                        ? '#ef9a9a'
                        : hasTimetableBg
                          ? hex
                          : '#d5d8de';
                    const offDayCenterText = (slot.timetableText ?? '').trim() || '공휴일';
                    const isNonClassStripeSlot = slot.timetableSlotKind === 'non_class';
                    const isMenuOpen =
                      !!dailyLessonMenu &&
                      weeklyBehaviorRecordKey(dailyLessonMenu.slot.dateIso, dailyLessonMenu.slot.slotIndex) ===
                        weeklyBehaviorRecordKey(slot.dateIso, slot.slotIndex);
                    const hasLessonRecord = d.progressClassRecordKind !== 'empty';
                    const hasBehaviorRecord = !!behaviorNoteBySlotIndex.get(slot.slotIndex);
                    const hatchClass =
                      slot.timetableSlotKind === 'non_class'
                        ? ' cp-base-timetable-cell-bg--non-class-stripes-over-fill'
                        : slot.timetableSlotKind === 'activity'
                          ? ' cp-base-timetable-cell-bg--activity-hatch-over-fill'
                          : slot.programKindLabel === '고정'
                            ? ' cp-base-timetable-cell-bg--fixed-program-hatch-over-fill'
                            : slot.programKindLabel === '프로'
                              ? ' cp-annual-project-grid-fill'
                              : '';

                    return (
                      <div
                        key={slot.slotIndex}
                        className={[
                          'cp-dashboard-slot',
                          'cp-dashboard-slot--daily-card',
                          noLesson ? 'cp-dashboard-slot--no-lesson' : 'cp-dashboard-slot--summary',
                          isCalendarHolidayRow && noLesson ? 'cp-dashboard-slot--calendar-off-day' : '',
                          isNonClassStripeSlot ? 'cp-dashboard-slot--nonclass-stripes' : '',
                          hasTimetableBg ? 'cp-dashboard-slot--has-tint' : '',
                          'cp-dashboard-slot--selectable',
                          isMenuOpen ? 'is-menu-open' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        style={{ ['--cp-dash-slot-color' as string]: slotTint }}
                        data-tutorial-dash="daily-slot"
                        data-dedicated="0"
                        data-no-lesson={noLesson ? '1' : '0'}
                        data-off-day={isCalendarHolidayRow ? '1' : '0'}
                        aria-expanded={isMenuOpen}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setPinnedLessonSlot(null);
                          setSelectedLessonSlotIndex(slot.slotIndex);
                          toggleDailyLessonMenu(slot, e.currentTarget);
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            setPinnedLessonSlot(null);
                            setSelectedLessonSlotIndex(slot.slotIndex);
                            toggleDailyLessonMenu(slot, e.currentTarget);
                          }
                        }}
                      >
                        <div className="cp-dashboard-slot__daily-split">
                          <DashboardPeriodRail periodLabel={periodLabel} timeLabel={timeLabel} />
                          <div
                            className={[
                              'cp-dashboard-slot__daily-main',
                              noLesson ? 'cp-dashboard-slot__no-lesson' : '',
                              isCalendarHolidayRow && noLesson ? 'cp-dashboard-slot__no-lesson--calendar-off' : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            role={noLesson ? 'status' : undefined}
                          >
                            {hatchClass ? (
                              <div
                                className={'cp-dashboard-slot__daily-hatch cp-base-timetable-cell-bg' + hatchClass}
                                aria-hidden
                              />
                            ) : null}
                            {(hasLessonRecord || hasBehaviorRecord) ? (
                              <div className="cp-dashboard-slot__record-tags">
                                {hasLessonRecord ? (
                                  <span
                                    className={
                                      'cp-dashboard-slot__record-tag ' +
                                      (d.progressClassRecordKind === 'eval_log'
                                        ? 'cp-dashboard-slot__record-tag--eval'
                                        : d.progressClassRecordKind === 'custom'
                                          ? 'cp-dashboard-slot__record-tag--custom'
                                          : 'cp-dashboard-slot__record-tag--lesson')
                                    }
                                  >
                                    {d.progressClassRecordKind === 'eval_log'
                                      ? '평가'
                                      : d.progressClassRecordKind === 'custom'
                                        ? '기록'
                                        : '수업'}
                                  </span>
                                ) : null}
                                {hasBehaviorRecord ? (
                                  <span className="cp-dashboard-slot__record-tag cp-dashboard-slot__record-tag--behavior">
                                    행동
                                  </span>
                                ) : null}
                              </div>
                            ) : null}
                            {noLesson ? (
                              isCalendarHolidayRow ? offDayCenterText : NO_LESSON_LABEL
                            ) : (
                              <DashboardLessonSummaryMain
                                pyeonje={pyeonje}
                                unitPart={unitPart}
                                learning={learn}
                                pagePart={pagePart}
                                evalCode={d.progressEvalCode}
                                evalLabel={d.progressEval}
                                evalNote={d.progressEvalNote}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : hasClassForDashboard && weekdayKey != null ? (
                <p className="cp-dashboard-placeholder" role="status" aria-live="polite">
                  수업·진도 데이터를 불러오는 중…
                </p>
              ) : (
                <p className="cp-dashboard-placeholder" aria-live="polite">
                  {EMPTY_SECTION_MESSAGE}
                </p>
              )}
            </div>
          </article>
          <article
            className="cp-dashboard-block cp-dashboard-block--planner-sheet cp-dashboard-todo-block"
            aria-labelledby="cp-dashboard-todo-section-title"
            data-tutorial-dash="daily-todo"
          >
            <h3 className="cp-dashboard-block__head cp-dashboard-todo-head" id="cp-dashboard-todo-section-title">
              <button
                type="button"
                className={
                  'cp-dashboard-todo-head-label' +
                  (todoExpanded ? ' is-todo-expanded' : '')
                }
                onClick={() => {
                  if (todoExpanded) setTodoExpanded(false);
                }}
                aria-expanded={todoExpanded}
                title={todoExpanded ? '2주 범위만 보기' : undefined}
              >
                TODO 리스트
              </button>
              <div className="cp-dashboard-todo-head-picker">
                <TodoNoteBoardPicker
                  boards={todoBoards}
                  selectedBoardId={todoSelectedBoardId}
                  open={todoBoardPickerOpen}
                  onOpenChange={setTodoBoardPickerOpen}
                  onSelect={(id) => selectDashboardTodoBoard(id)}
                  portalMenu
                />
              </div>
              <div
                className="cp-dashboard-todo-head-actions"
                data-tutorial-dash="daily-todo-actions"
              >
                <button
                  type="button"
                  className="cp-dashboard-todo-add-btn cp-dashboard-todo-add-btn--labeled"
                  data-tutorial-dash="daily-todo-add"
                  onClick={() => void openTodoAddModal()}
                  aria-label="TODO 추가"
                  title="TODO 추가"
                >
                  <span className="cp-dashboard-todo-add-btn__plus" aria-hidden>+</span>
                  노트 추가
                </button>
              </div>
            </h3>
            <div className="cp-dashboard-block__body">
              {todoLoading ? (
                <p className="cp-dashboard-placeholder" role="status" aria-live="polite">
                  ToDo 노트 불러오는 중…
                </p>
              ) : todoError ? (
                <p className="cp-dashboard-placeholder" role="alert">
                  {todoError}
                </p>
              ) : dashboardTodoRowsAllIncomplete.length === 0 ? (
                <p className="cp-dashboard-placeholder" aria-live="polite">
                  표시할 미완료 ToDo가 없습니다.
                </p>
              ) : (
                <>
                  {dashboardTodoTiles.length > 0 ? (
                    <div className="cp-dashboard-todo-list cp-dashboard-todo-postits" role="list" aria-label="미완료 ToDo 목록">
                      {dashboardTodoTiles.map((tile) => {
                        const item = tile.item;
                        const category =
                          todoCategories.find((c) => c.id === item.category_id) ?? null;
                        const gaugeProgress = holdGauge?.itemId === item.id ? holdGauge.progress : 0;
                        const isSelected =
                          selectedTodoItemId === item.id ||
                          selectedTodoItemId === tile.id ||
                          (item.routineParentId != null && selectedTodoItemId === item.routineParentId);
                        const memo = joinTodoMemo(item.title, item.body).trim();
                        const misc = (item.misc ?? '').trim();
                        const catColor = (category?.color ?? '').trim();
                        const isDueToday =
                          item.due_kind === 'day' && String(item.due_date ?? '').trim() === calendarTodayIso;
                        const isFavorite = isTodoNoteFavorite(item);
                        const canCopy = !tile.isRoutineStack && !item.is_routine && !item.routineParentId;
                        const stackLayers = tile.isRoutineStack
                          ? Math.min(TODO_POSTIT_STACK_LAYER_MAX, Math.max(0, tile.stackCount - 1))
                          : 0;
                        return (
                        <div
                          key={tile.id}
                          className={`cp-todo-note-postit-card cp-dashboard-todo-item${
                            tile.isRoutineStack ? ' is-routine-stack' : ''
                          }${gaugeProgress > 0 ? ' is-holding' : ''}${isSelected ? ' is-selected' : ''}${
                            isDueToday ? ' is-due-today' : ''
                          }${isFavorite ? ' is-favorite' : ''}`}
                          role="listitem"
                          aria-selected={isSelected}
                          style={{ ['--todo-postit-cat' as string]: catColor || '#e7c45a' }}
                          onPointerDown={(e) => {
                            if (e.pointerType === 'mouse' && e.button !== 0) return;
                            if (isDashboardTodoActionTarget(e.target)) return;
                            skipTodoCardOpenRef.current = false;
                            isDraggingRef.current = false;
                            
                            // 포인터 시작 위치 기록
                            pointerStartPositionRef.current = {
                              x: e.clientX,
                              y: e.clientY,
                              itemId: item.id
                            };
                            
                            e.preventDefault();
                            try {
                              e.currentTarget.setPointerCapture(e.pointerId);
                            } catch {
                              /* noop */
                            }
                            scheduleHoldGauge(item.id);
                          }}
                          onPointerMove={(e) => {
                            const startPos = pointerStartPositionRef.current;
                            if (startPos && startPos.itemId === item.id) {
                              const dx = e.clientX - startPos.x;
                              const dy = e.clientY - startPos.y;
                              const distance = Math.sqrt(dx * dx + dy * dy);
                              
                              // 10px 이상 이동하면 드래그로 간주
                              if (distance > 10) {
                                isDraggingRef.current = true;
                              }
                            }
                          }}
                          onPointerUp={(e) => {
                            const fromAction = isDashboardTodoActionTarget(e.target);
                            const didComplete = holdCompletedRef.current;
                            const wasDragging = isDraggingRef.current;
                            
                            releaseTodoItemPointer(item.id, e.currentTarget, e.pointerId);
                            
                            // 드래그 상태 초기화
                            pointerStartPositionRef.current = null;
                            isDraggingRef.current = false;
                            
                            // 드래그 중이 아니었고, 액션 버튼이 아니며, 홀드 완료도 아닌 경우에만 모달 열기
                            if (!didComplete && !fromAction && !wasDragging) {
                              openTodoEditModal(item.id);
                            }
                          }}
                          onPointerCancel={(e) => {
                            releaseTodoItemPointer(item.id, e.currentTarget, e.pointerId);
                            // 드래그 상태 초기화
                            pointerStartPositionRef.current = null;
                            isDraggingRef.current = false;
                          }}
                          onPointerLeave={(e) => {
                            if (holdDelayTimerRef.current != null || holdStartRef.current?.itemId === item.id) {
                              releaseTodoItemPointer(item.id, e.currentTarget, e.pointerId);
                              // 드래그 상태 초기화
                              pointerStartPositionRef.current = null;
                              isDraggingRef.current = false;
                            }
                          }}
                          onContextMenu={(e) => e.preventDefault()}
                        >
                          {Array.from({ length: stackLayers }, (_, i) => {
                            const layer = stackLayers - i;
                            return (
                              <span
                                key={layer}
                                className={
                                  'cp-todo-note-postit-stack-layer' + (layer === 1 ? ' is-fan-a' : ' is-fan-b')
                                }
                                aria-hidden
                              />
                            );
                          })}
                          {stackLayers > 0 ? <span className="cp-todo-note-postit-face" aria-hidden /> : null}
                          <div
                            className="cp-dashboard-todo-hold-gauge"
                            style={{ transform: `scaleX(${gaugeProgress})` }}
                            aria-hidden
                          />
                          <header className="cp-todo-note-postit-card-head">
                            <span className="cp-todo-note-postit-date">
                              {formatTodoPostitDate(item.due_kind, item.due_date)}
                            </span>
                            <TodoPostitCardActions
                              canCopy={canCopy}
                              isFavorite={isFavorite}
                              onActionPointerDown={() => {
                                skipTodoCardOpenRef.current = true;
                              }}
                              onDuplicate={canCopy ? () => void handleDuplicateTodo(item.id) : undefined}
                              onToggleFavorite={() =>
                                void handleToggleTodoFavorite(tile.isRoutineStack ? tile.id : item.id)
                              }
                            />
                          </header>
                          <div className="cp-todo-note-postit-memo">
                            {memo ? <TodoChecklistText text={memo} /> : '메모 없음'}
                          </div>
                          {misc ? <div className="cp-todo-note-postit-misc">{misc}</div> : null}
                        </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="cp-dashboard-placeholder" role="status" aria-live="polite">
                      이 기간(오늘부터 약 2주) 안에는 표시할 미완료가 없습니다. 아래 더보기에서 전체 TODO를 확인할 수 있습니다.
                    </p>
                  )}
                  {showTodoLoadMore ? (
                    <div className="cp-dashboard-todo-more-wrap">
                      <button
                        type="button"
                        className="cp-btn-secondary cp-dashboard-todo-more-btn"
                        onClick={() => setTodoExpanded(true)}
                        aria-label="전체 TODO 보기"
                      >
                        더보기
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </article>
        </div>
        )}
      </div>
      {yearCalendarModalOpen
        ? createPortal(
            <div
              className="cp-modal-backdrop cp-dash-year-cal-backdrop"
              role="presentation"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) setYearCalendarModalOpen(false);
              }}
            >
              <div
                className="cp-dash-year-cal-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="cp-dash-year-cal-title"
                data-tutorial-dash="year-cal-modal"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div className="cp-dash-year-cal-modal-head">
                  <h3 id="cp-dash-year-cal-title">달력</h3>
                  <button
                    type="button"
                    className="cp-dash-year-cal-modal-close"
                    data-tutorial-dash="year-cal-close"
                    aria-label="달력 닫기"
                    onClick={() => setYearCalendarModalOpen(false)}
                  >
                    ×
                  </button>
                </div>
                <div className="cp-dash-year-cal-modal-body">
                  <DashboardYearCalendars
                    academicYear={plannerAcademicYear}
                    holidayIsoSet={dashboardHolidayIsoSet}
                    holidayNoteByIso={dashboardHolidayNoteByIso}
                    termRanges={
                      (lessonBundle?.terms?.length ? lessonBundle.terms : lessonTerms) ?? []
                    }
                    todayIso={toISODate(new Date())}
                    selectedIso={toISODate(viewDate)}
                    onSelectDate={selectDateFromYearCalendar}
                  />
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
      {todoEditModalOpen && todoEditDraft ? (
        <TodoNoteItemAddModal
          open={todoEditModalOpen}
          draft={todoEditDraft}
          categories={todoCategories}
          categoryInput={todoEditCategoryInput}
          saving={todoEditSaving}
          title="TODO 수정"
          saveLabel="저장"
          autoFocusMemo={false}
          usePortal
          showHeaderCompleteToggle
          onToggleComplete={() => {
            patchTodoEditDraft({ completed: !todoEditDraft.completed });
          }}
          completeMark={todoEditDraft.completed ? 'v' : null}
          onDismissBackdrop={closeTodoEditModal}
          onCategoryInput={(v) => {
            setTodoEditCategoryInput(v);
            if (v.trim().length >= 2) {
              const hit = todoCategories.find((c) => c.name.toLowerCase().startsWith(v.trim().toLowerCase()));
              if (hit) patchTodoEditDraft({ category_id: hit.id });
            }
          }}
          onPickCategory={(cid) => {
            patchTodoEditDraft({ category_id: cid });
            const c = cid ? todoCategories.find((x) => x.id === cid) : null;
            setTodoEditCategoryInput(c?.name ?? '');
          }}
          onDueDate={(iso) => patchTodoEditDraft({ due_date: iso })}
          onDueSwitchToMonthOnly={(iso) => patchTodoEditDraft({ due_kind: 'month', due_date: iso })}
          onDueSwitchToDay={(iso) => patchTodoEditDraft({ due_kind: 'day', due_date: iso })}
          onMemoPatch={(title, body) => patchTodoEditDraft({ title, body })}
          onMisc={(v) => patchTodoEditDraft({ misc: v })}
          onCancel={closeTodoEditModal}
          onSave={() => void submitTodoEdit()}
          onDelete={() => void submitTodoDelete()}
        />
      ) : null}
      {todoRoutineModalOpen && todoRoutineDraft ? (
        <TodoRoutineCreateModal
          open={todoRoutineModalOpen}
          draft={todoRoutineDraft}
          categories={todoCategories}
          categoryInput={todoRoutineCategoryInput}
          saving={todoRoutineSaving}
          mode="edit"
          initialRoutine={todoRoutineDraft.routine ?? null}
          occurrenceYmd={todoRoutineOccurrenceYmd}
          completeToggleMode={todoRoutineOccurrenceYmd ? 'always' : 'never'}
          completeMark={todoRoutineCompleteMark}
          onToggleComplete={() => {
            if (!todoRoutineDraft) return;
            setTodoRoutineCompleteTouched(true);
            if (todoRoutineDraft.completed) {
              setTodoRoutineDraft((d) => (d ? { ...d, completed: false } : d));
              setTodoRoutineCompleteMark(null);
            } else {
              setTodoRoutineDraft((d) => (d ? { ...d, completed: true } : d));
              setTodoRoutineCompleteMark('v');
            }
          }}
          onDelete={() => void submitTodoRoutineDelete()}
          onDismissBackdrop={closeTodoRoutineModal}
          onCategoryInput={(v) => {
            setTodoRoutineCategoryInput(v);
            if (v.trim().length >= 2) {
              const hit = todoCategories.find((c) => c.name.toLowerCase().startsWith(v.trim().toLowerCase()));
              if (hit) setTodoRoutineDraft((d) => (d ? { ...d, category_id: hit.id } : d));
            }
          }}
          onPickCategory={(cid) => {
            setTodoRoutineDraft((d) => (d ? { ...d, category_id: cid } : d));
            const c = cid ? todoCategories.find((x) => x.id === cid) : null;
            setTodoRoutineCategoryInput(c?.name ?? '');
          }}
          onMemoPatch={(title, body) => setTodoRoutineDraft((d) => (d ? { ...d, title, body } : d))}
          onMisc={(v) => setTodoRoutineDraft((d) => (d ? { ...d, misc: v } : d))}
          onCancel={closeTodoRoutineModal}
          onSave={(payload) => void submitTodoRoutineEdit(payload)}
        />
      ) : null}
      {todoAddModalOpen && todoAddDraft ? (
        <TodoNoteItemAddModal
          open={todoAddModalOpen}
          draft={todoAddDraft}
          categories={todoCategories}
          categoryInput={todoAddCategoryInput}
          saving={todoAddSaving}
          title={todoAddIsDuplicate ? 'TODO 복제' : 'TODO 추가'}
          saveLabel={todoAddIsDuplicate ? '저장' : '추가'}
          usePortal
          onDismissBackdrop={closeTodoAddModal}
          onCategoryInput={(v) => {
            setTodoAddCategoryInput(v);
            if (v.trim().length >= 2) {
              const hit = todoCategories.find((c) => c.name.toLowerCase().startsWith(v.trim().toLowerCase()));
              if (hit) patchTodoAddDraft({ category_id: hit.id });
            }
          }}
          onPickCategory={(cid) => {
            patchTodoAddDraft({ category_id: cid });
            const c = cid ? todoCategories.find((x) => x.id === cid) : null;
            setTodoAddCategoryInput(c?.name ?? '');
          }}
          onDueDate={(iso) => patchTodoAddDraft({ due_date: iso })}
          onDueSwitchToMonthOnly={(iso) => patchTodoAddDraft({ due_kind: 'month', due_date: iso })}
          onDueSwitchToDay={(iso) => patchTodoAddDraft({ due_kind: 'day', due_date: iso })}
          onMemoPatch={(title, body) => patchTodoAddDraft({ title, body })}
          onMisc={(v) => patchTodoAddDraft({ misc: v })}
          onCancel={closeTodoAddModal}
          onSave={() => void submitTodoAdd()}
        />
      ) : null}
      {lessonRecordModalOpen && selectedLessonSlot ? createPortal(
        <div
          className="cp-modal-backdrop"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !lessonRecordSaving) setLessonRecordModalOpen(false);
          }}
        >
          <div
            className={
              'cp-program-note-popup cp-program-note-popup--with-head cp-program-note-popup--dashboard-lesson' +
              (lessonRecordModalExpanded ? ' cp-program-note-popup-expanded' : '')
            }
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-dashboard-lesson-record-title"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cp-program-note-popup-stack">
              <div className="cp-program-note-popup-head">
                <button
                  type="button"
                  className="cp-program-note-popup-expand-btn"
                  aria-label={lessonRecordModalExpanded ? '기본 크기로 축소' : '확장 모달로 보기'}
                  title={lessonRecordModalExpanded ? '기본 크기로 축소' : '확장 모달로 보기'}
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onClick={() => setLessonRecordModalExpanded((prev) => !prev)}
                >
                  <img
                    src={lessonRecordModalExpanded ? '/Minimize.svg' : '/Expand.svg'}
                    alt=""
                    aria-hidden
                    className="cp-program-note-popup-expand-icon"
                  />
                </button>
              </div>
              <div className="cp-program-eval-choice-tabs">
                <div className="cp-annual-progress-tabs cp-annual-project-term-tabs cp-program-class-rec-choice-row-half" role="group">
                  <button
                    type="button"
                    className={'cp-annual-progress-tab cp-program-class-rec-tab-lesson' + (lessonRecordTab === 'lesson' ? ' is-active' : '')}
                    onClick={() => { setLessonRecordError(null); setLessonRecordTab('lesson'); }}
                  >
                    수업기록
                  </button>
                  <button
                    type="button"
                    className={'cp-annual-progress-tab cp-program-class-rec-tab-eval_log' + (lessonRecordTab === 'eval_log' ? ' is-active' : '')}
                    onClick={() => { setLessonRecordError(null); setLessonRecordTab('eval_log'); }}
                  >
                    평가기록
                  </button>
                </div>
                <div className="cp-program-note-popup-context-wrap">
                  <p className="cp-program-note-popup-context cp-program-note-popup-context-primary" id="cp-dashboard-lesson-record-title">
                    {lessonRecordContextLine}
                  </p>
                  {lessonRecordUnitLine ? (
                    <p className="cp-program-note-popup-context cp-program-note-popup-context-secondary">
                      {lessonRecordUnitLine}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="cp-program-note-popup-field cp-program-class-rec-memo-field">
                <div
                  className={
                    'cp-class-rec-member-toolbar' +
                    (lessonRecordTab === 'eval_log'
                      ? ' cp-class-rec-member-toolbar-eval'
                      : ' cp-class-rec-member-toolbar-lesson')
                  }
                >
                  <RecordMemberGroupSelect
                    id="cp-dashboard-lesson-member-group"
                    layout={memberGradeLayout}
                    value={lessonRecordMemberGroupId}
                    requireGroup
                    onChange={setLessonRecordMemberGroupId}
                    disabled={lessonRecordSaving}
                    wrapClassName="cp-class-rec-member-group-select-half"
                  />
                  <button
                    type="button"
                    className="cp-btn-secondary cp-class-rec-member-tag-gen-btn"
                    disabled={
                      lessonRecordSaving ||
                      !(lessonRecordMemberGroupId ?? '').trim() ||
                      !firstRecordGroupId(memberGradeLayout)
                    }
                    onClick={handleGenerateLessonMemberTags}
                  >
                    멤버 태그 생성
                  </button>
                </div>
                {lessonRecordTab === 'lesson' ? (
                  <ClassRecordMemoNoteEditor
                    key={`${selectedLessonSlot.annualCellKey}:lesson`}
                    ref={lessonRecordMemoEditorRef}
                    value={lessonRecordNote}
                    onChange={setLessonRecordNote}
                    members={lessonRecordMembers}
                    applyBulkMemberTagRules={!!lessonRecordMemberGroupId}
                    sealedLabelByToken={sealedLabelByToken}
                    onHashtagTagFinalized={finalizeLessonHashtagForMemberGroup}
                    placeholder="수업기록 메모"
                  />
                ) : (
                  <ClassRecordMemoNoteEditor
                    key={`${selectedLessonSlot.annualCellKey}:eval`}
                    ref={lessonRecordEvalEditorRef}
                    value={lessonRecordNoteEval}
                    onChange={(note) => {
                      setLessonRecordNoteEval(note);
                      if (!note.trim()) setLessonRecordEvalMemoMemberGroupId(null);
                    }}
                    members={lessonRecordMembers}
                    applyBulkMemberTagRules={!!(lessonRecordEvalMemoMemberGroupId ?? '').trim()}
                    sealedLabelByToken={sealedLabelByToken}
                    onHashtagTagFinalized={finalizeLessonHashtagForMemberGroup}
                    placeholder="평가기록 메모"
                  />
                )}
              </div>
              {lessonRecordError ? (
                <p className="cp-program-class-rec-modal-error" role="alert">{lessonRecordError}</p>
              ) : null}
              <div className="cp-program-note-popup-actions">
                <button
                  type="button"
                  className="cp-program-note-confirm-btn"
                  onClick={() => setLessonRecordModalOpen(false)}
                  disabled={lessonRecordSaving}
                >
                  닫기
                </button>
                <button
                  type="button"
                  className="cp-program-note-confirm-btn cp-program-note-confirm-btn--primary cp-record-save-btn"
                  onClick={() => void saveLessonRecord()}
                  disabled={lessonRecordSaving || !dashboardLessonRecordDirty}
                >
                  {lessonRecordSaving ? '저장 중…' : dashboardLessonRecordDirty ? '저장' : '변경사항 없음'}
                </button>
                <DeleteHoldButton
                  disabled={lessonRecordSaving || !lessonRecordCanDelete}
                  disabledTitle="삭제할 수업·평가 기록이 없습니다"
                  disabledAriaLabel="삭제할 수업·평가 기록이 없습니다"
                  ariaLabel="수업·평가 기록 삭제 (길게 누르기)"
                  onDelete={() => void saveLessonRecord({ delete: true })}
                />
              </div>
            </div>
          </div>
        </div>,
        document.body
      ) : null}
      {lessonTagGroupPick ? createPortal(
        <div
          className="cp-modal-backdrop"
          role="presentation"
          onMouseDown={(ev) => { if (ev.target === ev.currentTarget) setLessonTagGroupPick(null); }}
        >
          <div className="cp-modal" role="dialog" aria-modal="true" onMouseDown={(ev) => ev.stopPropagation()}>
            <h3>멤버 그룹 선택</h3>
            <p className="page-desc">
              &quot;{lessonTagGroupPick.keyword}&quot;에 해당하는 학생이 여러 그룹에 있습니다. 기록에 사용할 학생을 선택해 주세요.
            </p>
            <div className="cp-modal-body cp-class-rec-tag-group-pick-list">
              {lessonTagGroupPick.candidates.map((c) => (
                <button
                  key={`${c.groupId}-${c.memberId}`}
                  type="button"
                  className="cp-btn-secondary cp-class-rec-tag-group-pick-item"
                  onClick={() => { setLessonRecordMemberGroupId(c.groupId); setLessonTagGroupPick(null); }}
                >
                  {c.groupName} · {c.memberName}
                </button>
              ))}
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setLessonTagGroupPick(null)}>취소</button>
            </div>
          </div>
        </div>,
        document.body
      ) : null}
      {lessonEvalTagGenConfirm ? createPortal(
        <div
          className="cp-modal-backdrop"
          role="presentation"
          onMouseDown={(ev) => {
            if (ev.target === ev.currentTarget) setLessonEvalTagGenConfirm(null);
          }}
        >
          <div className="cp-modal" role="dialog" aria-modal="true" onMouseDown={(ev) => ev.stopPropagation()}>
            <h3>멤버 태그 생성</h3>
            <p className="page-desc">
              태그(#이름)가 아닌 입력 내용은 모두 초기화되고, 선택한 그룹의 멤버 태그만 남습니다. 계속할까요?
            </p>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setLessonEvalTagGenConfirm(null)}>
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => {
                  applyLessonMemberTags(
                    lessonEvalTagGenConfirm.nextNote,
                    lessonEvalTagGenConfirm.sourceGroupId
                  );
                  setLessonEvalTagGenConfirm(null);
                }}
              >
                초기화 후 생성
              </button>
            </div>
          </div>
        </div>,
        document.body
      ) : null}
      {behaviorNoteModalOpen && selectedLessonSlot ? createPortal(
        <div
          className="cp-modal-backdrop"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !behaviorNoteSaving) setBehaviorNoteModalOpen(false);
          }}
        >
          <div
            className={
              'cp-program-note-popup cp-program-note-popup--with-head cp-program-note-popup--dashboard-behavior' +
              (behaviorNoteModalExpanded ? ' cp-program-note-popup-expanded' : '')
            }
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-dashboard-behavior-note-title"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cp-program-note-popup-stack">
              <div className="cp-program-note-popup-head">
                <button
                  type="button"
                  className="cp-program-note-popup-expand-btn"
                  aria-label={behaviorNoteModalExpanded ? '기본 크기로 축소' : '확장 모달로 보기'}
                  title={behaviorNoteModalExpanded ? '기본 크기로 축소' : '확장 모달로 보기'}
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onClick={() => setBehaviorNoteModalExpanded((prev) => !prev)}
                >
                  <img
                    src={behaviorNoteModalExpanded ? '/Minimize.svg' : '/Expand.svg'}
                    alt=""
                    aria-hidden
                    className="cp-program-note-popup-expand-icon"
                  />
                </button>
              </div>
              <div className="cp-program-eval-choice-tabs">
                <div className="cp-annual-progress-tabs cp-annual-project-term-tabs cp-program-class-rec-choice-row-half" role="group">
                  <button type="button" className="cp-annual-progress-tab cp-program-class-rec-tab-behavior is-active" tabIndex={-1}>
                    행동특성
                  </button>
                </div>
                <div className="cp-program-note-popup-context-wrap">
                  <p className="cp-program-note-popup-context cp-program-note-popup-context-primary" id="cp-dashboard-behavior-note-title">
                    {behaviorNoteContextLine}
                  </p>
                </div>
              </div>
              <div className="cp-program-note-popup-field cp-program-class-rec-memo-field">
                <div className="cp-class-rec-member-toolbar cp-class-rec-member-toolbar-lesson">
                  <RecordMemberGroupSelect
                    id="cp-dashboard-behavior-member-group"
                    layout={memberGradeLayout}
                    value={behaviorNoteMemberGroupId}
                    onChange={setBehaviorNoteMemberGroupId}
                    disabled={behaviorNoteSaving}
                  />
                </div>
                <ClassRecordMemoNoteEditor
                  key={`${selectedLessonSlot.annualCellKey}:behavior`}
                  ref={behaviorMemoEditorRef}
                  value={behaviorNote}
                  onChange={setBehaviorNote}
                  members={behaviorNoteMembers}
                  applyBulkMemberTagRules={!!behaviorNoteMemberGroupId}
                  sealedLabelByToken={sealedLabelByToken}
                  onHashtagTagFinalized={finalizeBehaviorHashtagForMemberGroup}
                  placeholder="행동특성을 입력해 주세요."
                />
              </div>
              {behaviorNoteError ? (
                <p className="cp-program-class-rec-modal-error" role="alert">{behaviorNoteError}</p>
              ) : null}
              <AiRecordFeedbackPanel
                ref={dashboardBehaviorAiRef}
                workspaceId={workspaceId}
                recordKey={selectedLessonSlot.annualCellKey}
                kind="behavior"
                onDirtyChange={setDashboardBehaviorAiDirty}
                embeddedText={
                  behaviorNoteByDateSlotKey.get(
                    weeklyBehaviorRecordKey(selectedLessonSlot.dateIso, selectedLessonSlot.slotIndex)
                  )?.ai_feedback ?? null
                }
                note={behaviorNote}
                contextLines={[behaviorNoteContextLine && `일시: ${behaviorNoteContextLine}`]}
                members={behaviorNoteMembers}
                allMembers={classMembers}
                disabled={behaviorNoteSaving}
              />
              <div className="cp-program-note-popup-actions">
                <button
                  type="button"
                  className="cp-program-note-confirm-btn"
                  onClick={() => setBehaviorNoteModalOpen(false)}
                  disabled={behaviorNoteSaving}
                >
                  닫기
                </button>
                <button
                  type="button"
                  className="cp-program-note-confirm-btn cp-program-note-confirm-btn--primary cp-record-save-btn"
                  onClick={() => {
                    const commitAi = dashboardBehaviorAiRef.current?.capture();
                    const aiFeedback = dashboardBehaviorAiRef.current?.getEmbedValue();
                    void saveBehaviorNote(aiFeedback).then((ok) =>
                      finalizeBehaviorAiFeedbackAfterSave({
                        ok,
                        commit: commitAi,
                        workspaceId: workspaceId ?? '',
                        recordKey: selectedLessonSlot.annualCellKey,
                        text: aiFeedback,
                      })
                    );
                  }}
                  disabled={behaviorNoteSaving || !dashboardBehaviorDirty}
                >
                  {behaviorNoteSaving ? '저장 중…' : dashboardBehaviorDirty ? '저장' : '변경사항 없음'}
                </button>
                <DeleteHoldButton
                  disabled={behaviorNoteSaving || !behaviorNoteCanDelete}
                  disabledTitle="삭제할 행동특성 기록이 없습니다"
                  disabledAriaLabel="삭제할 행동특성 기록이 없습니다"
                  ariaLabel="행동특성 기록 삭제 (길게 누르기)"
                  onDelete={() => {
                    void dashboardBehaviorAiRef.current?.remove(['behavior']);
                    void deleteBehaviorNote();
                  }}
                />
              </div>
            </div>
          </div>
        </div>,
        document.body
      ) : null}
      {behaviorTagGroupPick ? createPortal(
        <div
          className="cp-modal-backdrop"
          role="presentation"
          onMouseDown={(ev) => { if (ev.target === ev.currentTarget) setBehaviorTagGroupPick(null); }}
        >
          <div className="cp-modal" role="dialog" aria-modal="true" onMouseDown={(ev) => ev.stopPropagation()}>
            <h3>멤버 그룹 선택</h3>
            <p className="page-desc">
              &quot;{behaviorTagGroupPick.keyword}&quot;에 해당하는 학생이 여러 그룹에 있습니다. 기록에 사용할 학생을 선택해 주세요.
            </p>
            <div className="cp-modal-body cp-class-rec-tag-group-pick-list">
              {behaviorTagGroupPick.candidates.map((c) => (
                <button
                  key={`${c.groupId}-${c.memberId}`}
                  type="button"
                  className="cp-btn-secondary cp-class-rec-tag-group-pick-item"
                  onClick={() => { setBehaviorNoteMemberGroupId(c.groupId); setBehaviorTagGroupPick(null); }}
                >
                  {c.groupName} · {c.memberName}
                </button>
              ))}
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setBehaviorTagGroupPick(null)}>취소</button>
            </div>
          </div>
        </div>,
        document.body
      ) : null}
      {isWeeklyLessonView && weeklyNotesMode ? (
        <button
          type="button"
          className="cp-fab-save cp-dash-weekly-notes-fab"
          onClick={() => void handleSaveDailyNotes()}
          disabled={dailyNotesSaving || !dailyNotesDirty || !userId}
          aria-label="일자별 기록 저장"
          title={dailyNotesDirty ? '저장하기' : '저장할 변경사항 없음'}
        >
          <img
            src={`${process.env.PUBLIC_URL || ''}/Save.svg`}
            alt=""
            className="cp-fab-save-icon"
          />
        </button>
      ) : null}
      {dailyLessonMenu ? (
        <DashboardWeeklyLessonActionMenu
          slot={dailyLessonMenu.slot}
          anchorRect={dailyLessonMenu.rect}
          onClose={() => setDailyLessonMenu(null)}
          onOpenPresentation={openWeeklySlotPresentation}
          onOpenProgram={openDashboardSlotProgram}
          onOpenLessonRecord={(slot) => openLessonRecordModal(slot)}
          onOpenBehaviorRecord={(slot) => openBehaviorNoteModal(slot)}
        />
      ) : null}
      <AutoStatusToast state={dailyNotesToast} />
      <DailyNoteLockModal
        open={weeklyNoteUnlockOpen}
        mode="note"
        busy={weeklyNoteUnlockBusy}
        error={weeklyNoteUnlockError}
        onClose={closeWeeklyNoteUnlock}
        onSubmit={(value) => {
          void handleWeeklyNoteUnlockSubmit(value);
        }}
      />
    </section>
  );
};

export default DashboardPage;
